import ko from "knockout";
import _ from "lodash";
import { flow, map, uniqBy } from "lodash/fp";
import Promise from "bluebird";
import moment from "moment";
import callService from "services/callService";
import wrapUpCodeService from "services/wrapupCodeService";
import LOG from "services/loggingService";
import userService from "services/userService";
import externalContactService from "services/externalContactService";
import analyticsService from "services/analyticsService";
import qualityAuth from "services/qualityAuth";
import qualityManagementService from "services/qualityManagementService";
import conversationsProtectionService from "services/conversationsProtectionService";
import navHelper from "services/navHelper";
import ViewModel from "viewModels/ViewModel";
import DateEditorView from "viewModels/policies/editors/DateEditorView";
import FileStateEnum from "viewModels/util/FileStateEnum";
import SparkVariables from "enums/SparkVariables";
import Icons from "enums/Icons";
import SiptracesView from "viewModels/SiptracesView";
import errorCallbackFactory from "util/errorCallbackFactory";
import qualityBroadcast from "services/qualityBroadcast";
import BroadCastEnum from "viewModels/util/BroadCastEnum";
import CategoriesView from "viewModels/CategoriesView";
import messageService from "services/messageService";
import screenMonitoringService from "services/screenMonitoringService";
import screenMonitoringUtils from "util/screenMonitoringUtils";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import speechAndTextAnalyticsService from "services/speechAndTextAnalyticsService";
import OverallSentimentResources from "enums/OverallSentimentResources";
import PercentGraphView from "viewModels/widgets/PercentGraphView";
import "components/copy-button";
import "components/collapsable-text";
import segmentUtils from "util/segmentUtils";
import EmpathyResources from "enums/EmpathyResources";
import ScreenMonitoringType from "enums/ScreenMonitoringType";
import {
    FULL_DATE_MEDIUM_TIME_OPTIONS,
    SHORT_DATE_OPTIONS,
} from "util/DateTimeHelper";
import uuidUtils from "util/uuidUtils";
import { setQueryParams } from "util/uriUtil";

/**
 * Safely converts participant attributes object to array format
 * @param {Object} attributes - The attributes object with key-value pairs
 * @returns {Array} Array of {name, value} objects or empty array if invalid
 */
const fetchParticipantDataAttributes = function (attributes) {
    if (
        attributes !== undefined &&
        attributes !== null &&
        _.isObject(attributes) &&
        !_.isArray(attributes) &&
        !_.isEmpty(attributes)
    ) {
        return Object.keys(attributes).map((key) => ({
            name: key,
            value: attributes[key],
        }));
    }
    return [];
};

const checkImages = function (avatarImages) {
    // if any image size has a "no_img" value then return false so the default image will show
    // the reason for returning false and not one with an image if it exists to to keep images consistent
    // in the template we care about the x96 size, for brevity sake
    return !_.includes(avatarImages(), "no_img");
};

function participantHasCalls(participant) {
    return participant.calls && participant.calls.length > 0;
}

function participantHasMessages(participant) {
    return participant.messages && participant.messages.length > 0;
}

function participantHasEmails(participant) {
    return participant.emails && participant.emails.length > 0;
}

function participantHasScreenMonitorings(participant) {
    const screenMonitorings = ko.unwrap(participant.screenMonitorings);
    return screenMonitorings && screenMonitorings.length > 0;
}

function participantHasConnectedCalls(participant) {
    return (
        participantHasCalls(participant) &&
        _.some(participant.calls, { state: "connected" })
    );
}

function participantHasConnectedMessages(participant) {
    return (
        participantHasMessages(participant) &&
        _.some(participant.messages, { state: "connected" })
    );
}

function participantHasConnectedEmails(participant) {
    return (
        participantHasEmails(participant) &&
        _.some(participant.emails, { state: "connected" })
    );
}

function participantHasAuthenticatedCommunication(
    participant,
    analyticsParticipant
) {
    // check CS participant
    if (participant) {
        const communications = _.flatMap([
            participant.calls,
            participant.emails,
            participant.chats,
            participant.messages,
        ]);
        if (
            communications.length > 0 &&
            _.some(communications, { authenticated: true })
        ) {
            return true;
        }
    }

    // check analytics participant
    if (
        analyticsParticipant &&
        analyticsParticipant.sessions &&
        analyticsParticipant.sessions.length > 0
    ) {
        if (_.some(analyticsParticipant.sessions, { authenticated: true })) {
            return true;
        }
    }
    return false;
}

//Gets initialized in constructor of ConversationDetailsView
let directionStrings = {};

const getLocalizedDirection = function (dir) {
    if (_.isEmpty(dir)) {
        return dir;
    }

    dir = dir.toLowerCase();

    if (dir.indexOf("inbound") > -1 && dir.indexOf("outbound") > -1) {
        return directionStrings.BOTH;
    } else if (dir === "inbound") {
        return directionStrings.INBOUND;
    } else if (dir === "outbound") {
        return directionStrings.OUTBOUND;
    }
};

const getWrapupName = function (wrapup) {
    let wrapupName = wrapup.name;
    if (!wrapupName && wrapup.code && _.startsWith(wrapup.code, "ININ-")) {
        wrapupName = wrapup.code;
    }
    return wrapupName;
};

const wrapUpCodeNamesById = {};

const MESSAGE_SUBTYPE_SMS = "sms";

// Conversation type constants
const ConversationType = {
    CALL: "call",
    MESSAGE: "message",
    EMAIL: "email",
    SCREEN_MONITORING: "screenMonitoring",
};

const ParticipantView = ViewModel.extend(function (
    conversationId,
    participant,
    analyticsParticipantV6,
    isCurrentUserOnCall,
    hasMonitorCallPermission,
    hasCoachCallPermission,
    hasBargeInCallPermission,
    hasMonitorMessagePermission,
    hasAgentLevelScreenMonitorPermission,
    hasConversationLevelScreenMonitorPermission,
    options,
    isAnyInternalParticipantMonitored,
    isAnyInternalParticipantCoached,
    isAnyInternalParticipantBargedIn,
    isExternalParticipantMonitored,
    isAlreadyCoached,
    isAlreadyMonitored,
    conversationMetrics,
    conversationMetricsErr,
    conversationType,
    conversationDivisions
) {
    const self = this;
    const endTime = ko.observable(participant.endTime);
    const monitoredParticipantId = ko.observable(
        participant.monitoredParticipantId
    );
    const coachedParticipantId = ko.observable(
        participant.coachedParticipantId
    );
    const screenMonitoringTargetUserIds = ko.computed(function () {
        // participant is a plain object, but screenMonitorings might be observable
        const screenMonitorings = ko.unwrap(participant.screenMonitorings);
        return screenMonitoringUtils.getAllUniqueTargetUserIds(
            screenMonitorings
        );
    });
    self.bargedParticipantId = ko.observable(participant.bargedParticipantId);
    self.isSupervisorBarged = ko.observable(
        participant.mediaRoles && participant.mediaRoles.includes("barge")
    );
    const hasCalls = ko.observable(participantHasCalls(participant));
    const hasMessages = ko.observable(participantHasMessages(participant));
    const hasEmails = ko.observable(participantHasEmails(participant));
    const hasScreenMonitorings = ko.observable(
        participantHasScreenMonitorings(participant)
    );
    const hasConnectedCalls = ko.observable(
        participantHasConnectedCalls(participant)
    );
    const hasConnectedMessages = ko.observable(
        participantHasConnectedMessages(participant)
    );
    const hasConnectedEmails = ko.observable(
        participantHasConnectedEmails(participant)
    );
    self.isAlreadyCoached = ko.observable(isAlreadyCoached);

    // Check if another user is already monitoring (only for message conversations)
    self.isAlreadyMonitored = ko.observable(isAlreadyMonitored);

    self.screenMonitorRequestInProgress = ko.observable(false);
    self.conversationId = conversationId;
    self.conversationDivisionIds = conversationDivisions;
    self.monitorRequestInProgress = ko.observable(false);
    self.coachRequestInProgress = ko.observable(false);
    self.bargeInRequestInProgress = ko.observable(false);
    self.name = participant.name;
    self.dnis = ko.i18n.formatPhoneAddress(participant.dnis);
    self.ani = ko.i18n.formatPhoneAddress(participant.ani);
    self.conversationMetrics = ko.observable(conversationMetrics);
    self.conversationMetricsErr = ko.observable(conversationMetricsErr);

    self.isWEM1418orWEM437Enabled =
        featureToggleService.isEnabled(
            FeatureToggle.WEM_1418_IDUI_SPARK_V4_FLARE
        ) ||
        featureToggleService.isEnabled(FeatureToggle.WEM_437_SCREEN_MONITORING);

    self.collapse = ko.observable(true);

    self.updateCollapse = function () {
        self.collapse(!self.collapse());
    };

    self.attributes = ko.observableArray(
        fetchParticipantDataAttributes(participant.attributes)
    );

    const conversationParticipant = ko.observable(participant);
    const analyticsParticipant = ko.observable(analyticsParticipantV6);

    self.hasAuthenticatedCommunication = ko.observable(
        participantHasAuthenticatedCommunication(
            participant,
            analyticsParticipantV6
        )
    );

    self.isScreenMonitoringEnabled = featureToggleService.isEnabled(
        FeatureToggle.WEM_437_SCREEN_MONITORING
    );

    // Expose Icons to templates for use in HTML bindings
    self.Icons = Icons;

    self.address = ko.i18n.formatPhoneAddress(participant.address);
    self.user = ko.observable();
    self.userImages = ko.observable();
    self.participantId = participant.id;
    self.deleteDate = ko.observable();
    self.archiveDate = ko.observable();
    self.userId = participant.userId;
    self.disconnectType = ko.observable();
    self.errorInfoCode = ko.observable();
    self.externalContact = ko.observable();
    self.externalContactId = participant.externalContactId;
    self.id = participant.id;
    self.isOpen = ko.observable(false);
    self.isExpanded = ko.observable(true);

    self.toggleExpandMonitorOrCoach = function () {
        self.isExpanded(!self.isExpanded());
    };

    let direction;
    let disconnectType;
    let errorInfo;

    if (
        !_.isEmpty(participant.calls) &&
        !_.isUndefined(participant.calls[0].direction)
    ) {
        direction = participant.calls[0].direction;
        disconnectType = participant.calls[0].disconnectType;
        errorInfo = participant.calls[0].errorInfo;
    } else if (!_.isEmpty(participant.chats)) {
        direction = participant.chats[0].direction;
        disconnectType = participant.chats[0].disconnectType;
        errorInfo = participant.chats[0].errorInfo;
    } else if (
        !_.isEmpty(participant.emails) &&
        !_.isUndefined(participant.emails[0].direction)
    ) {
        direction = participant.emails[0].direction;
        const lastEmail = participant.emails[participant.emails.length - 1];
        disconnectType = lastEmail.disconnectType;
        errorInfo = lastEmail.errorInfo;
    } else if (!_.isEmpty(participant.messages)) {
        const lastMessage =
            participant.messages[participant.messages.length - 1];
        disconnectType = lastMessage.disconnectType;
        errorInfo = lastMessage.errorInfo;
    }

    self.direction = getLocalizedDirection(direction);

    if (disconnectType) {
        let disconnectTypeKey;
        const PREFIX = "conversationTimeline.tooltip.disconnectType.";
        disconnectType = disconnectType.toLowerCase();

        if (disconnectType.indexOf("transfer") !== -1) {
            disconnectTypeKey = PREFIX + "transfer";
        } else if (ko.i18n.keyExists(PREFIX + disconnectType)) {
            disconnectTypeKey = PREFIX + disconnectType;
        } else {
            disconnectTypeKey = PREFIX + "other";
        }

        disconnectType = ko.i18n.renderString(disconnectTypeKey);
        self.disconnectType(disconnectType);
    }

    if (errorInfo && errorInfo.code) {
        errorInfo = errorInfo.code;
        let errorInfoKey;
        const PREFIX = "conversationDetails.errorInfo.code.";

        if (ko.i18n.keyExists(PREFIX + errorInfo)) {
            errorInfoKey = PREFIX + errorInfo;
            errorInfo = ko.i18n.renderString(errorInfoKey);
            self.errorInfoCode(errorInfo);
        }
    }

    if (options.expandUser && participant.userId) {
        userService
            .getUser({ userId: participant.userId, state: "deleted" })
            .then(function (user) {
                self.user(user);
                if (
                    user.avatarImages &&
                    user.avatarImages() &&
                    checkImages(user.avatarImages)
                ) {
                    self.userImages(user.avatarImages());
                    const imagesMap = {};
                    imagesMap[participant.userId] = user.avatarImages();
                    qualityBroadcast.publish(
                        conversationId,
                        BroadCastEnum.USER_IMAGES_AVAILABLE,
                        imagesMap
                    );
                }
            });
    }

    if (
        participant.externalContactId &&
        externalContactService.canViewExternalContact(
            ko.unwrap(self.conversationDivisionIds)
        )
    ) {
        externalContactService
            .getCachedExternalContact(participant.externalContactId)
            .then(self.externalContact)
            .catch(function (err) {
                errorCallbackFactory.createCallback({
                    namespace: "playback.externalParticipant",
                    logError: false, // log in the supplied callback function instead
                    strategies: {
                        404: function () {
                            LOG.info(`External contact is not found.`);
                        },
                        403: function () {
                            LOG.info(
                                `Your account does not have permission to view the external contact.`
                            );
                        },
                        DEFAULT: function () {
                            LOG.error(
                                `Error fetching external contact details for participant`,
                                err
                            );
                        },
                    },
                })(err);
            });
    }

    self.disconnectTypeDisplay = self.computed(function () {
        if (self.disconnectType() && self.errorInfoCode()) {
            return `${self.disconnectType()} - ${self.errorInfoCode()}`;
        }
        return self.disconnectType();
    });

    // check to see if there is an active user call that is monitoring this participant
    const monitoredCall = self.computed(function () {
        return _.find(callService.activeUserCalls(), function (call) {
            return (
                self.conversationId === call.id &&
                call.isMonitor() &&
                call.isConnected() &&
                call.participantForCurrentUser().monitoredParticipantId() ===
                    participant.id
            );
        });
    });

    // check to see if there is an active user call that is coaching this participant
    const coachedCall = self.computed(function () {
        return _.find(callService.activeUserCalls(), function (call) {
            return (
                self.conversationId === call.id &&
                call.isCoach() &&
                call.isConnected() &&
                call.participantForCurrentUser().coachedParticipantId() ===
                    participant.id
            );
        });
    });

    // check to see if there is an active user call that is bargedIn
    const bargedInCall = self.computed(function () {
        return _.find(callService.activeUserCalls(), function (call) {
            return (
                self.conversationId === call.id &&
                call.isBargeIn() &&
                call.isConnected() &&
                call.participantForCurrentUser().bargedParticipantId() ===
                    participant.id
            );
        });
    });

    const monitoredMessage = self.computed(function () {
        return _.find(messageService.activeUserMessages(), function (message) {
            return (
                self.conversationId === message.id &&
                message.isMonitor() &&
                message.isConnected() &&
                message.participantForCurrentUser().monitoredParticipantId() ===
                    participant.id
            );
        });
    });

    const monitoredScreen = self.computed(function () {
        if (!self.isScreenMonitoringEnabled) {
            return null;
        }
        return _.find(
            screenMonitoringService.activeUserScreenMonitors(),
            function (screenMonitor) {
                const screenMonitorParticipant =
                    screenMonitor.participantForCurrentUser();
                const screenMonitorings = screenMonitorParticipant
                    ? screenMonitorParticipant.screenMonitorings
                    : null;
                const targetUserId =
                    screenMonitoringUtils.getConnectedTargetUserId(
                        screenMonitorings
                    );
                return (
                    self.conversationId === screenMonitor.id &&
                    screenMonitor.isScreenMonitor() &&
                    screenMonitor.isConnected() &&
                    targetUserId === participant.userId
                );
            }
        );
    });

    const connectedScreenMonitoring = self.computed(function () {
        if (!monitoredScreen()) {
            return null;
        }
        const supervisorParticipant =
            monitoredScreen().participantForCurrentUser();
        return screenMonitoringUtils.getConnectedScreenMonitoring(
            supervisorParticipant?.screenMonitorings
        );
    });

    const screenMonitoringId = self.computed(() =>
        connectedScreenMonitoring()?.screenMonitoring()?.id()
    );
    const screenMonitoringType = self.computed(() =>
        connectedScreenMonitoring()?.monitoringType()
    );

    self.updateParticipant = function (
        updatedParticipant,
        updatedAnalyticsParticipant,
        isAlreadyCoached,
        isAlreadyMonitored
    ) {
        if (participant.id === updatedParticipant.id) {
            conversationParticipant(updatedParticipant);
            endTime(updatedParticipant.endTime);
            monitoredParticipantId(updatedParticipant.monitoredParticipantId);
            coachedParticipantId(updatedParticipant.coachedParticipantId);
            self.bargedParticipantId(updatedParticipant.bargedParticipantId);
            self.isSupervisorBarged(
                updatedParticipant.mediaRoles &&
                    updatedParticipant.mediaRoles.includes("barge")
            );
            hasCalls(participantHasCalls(updatedParticipant));
            hasMessages(participantHasMessages(updatedParticipant));
            hasEmails(participantHasEmails(updatedParticipant));
            hasScreenMonitorings(
                participantHasScreenMonitorings(updatedParticipant)
            );
            hasConnectedCalls(participantHasConnectedCalls(updatedParticipant));
            hasConnectedMessages(
                participantHasConnectedMessages(updatedParticipant)
            );
            hasConnectedEmails(
                participantHasConnectedEmails(updatedParticipant)
            );
            self.hasAuthenticatedCommunication(
                participantHasAuthenticatedCommunication(
                    participant,
                    updatedAnalyticsParticipant
                )
            );
            self.isAlreadyCoached(isAlreadyCoached);
            self.isAlreadyMonitored(isAlreadyMonitored);

            // Update participant attributes if present in the update
            const updatedAttributes = fetchParticipantDataAttributes(
                updatedParticipant.attributes
            );
            if (updatedAttributes.length > 0) {
                self.attributes(updatedAttributes);
            }
        } else {
            LOG.error(
                "The updatedParticipant id does not match the current participant id.",
                {
                    updatedParticipantId: updatedParticipant.id,
                    currentParticipantId: participant.id,
                }
            );
        }

        if (
            updatedAnalyticsParticipant &&
            participant.id === updatedAnalyticsParticipant.participantId
        ) {
            analyticsParticipant(updatedAnalyticsParticipant);
        }
    };

    self.isCallConversation = self.computed(function () {
        return (
            conversationType &&
            conversationType().includes(ConversationType.CALL)
        );
    });

    self.isMessageConversation = self.computed(function () {
        return (
            conversationType &&
            conversationType().includes(ConversationType.MESSAGE)
        );
    });

    self.isEmailConversation = self.computed(function () {
        return (
            conversationType &&
            conversationType().includes(ConversationType.EMAIL)
        );
    });

    self.isScreenMonitoringConversation = self.computed(function () {
        return (
            conversationType &&
            conversationType().includes(ConversationType.SCREEN_MONITORING)
        );
    });

    self.canMonitor = self.computed(function () {
        return (
            !isCurrentUserOnCall() &&
            ((!endTime() && hasCalls()) || hasConnectedCalls())
        );
    });

    self.isPermittedToMonitor = self.computed(function () {
        return hasMonitorCallPermission() && hasCalls();
    });

    self.isMonitored = self.computed(function () {
        return !!monitoredCall();
    });

    self.isMonitor = self.computed(function () {
        return !!monitoredParticipantId() && hasCalls();
    });

    self.canCoach = self.computed(function () {
        return (
            !isCurrentUserOnCall() &&
            ((!endTime() && hasCalls()) || hasConnectedCalls())
        );
    });

    self.isPermittedToCoach = self.computed(function () {
        return hasCoachCallPermission() && hasCalls();
    });

    self.isCoached = self.computed(function () {
        return !!coachedCall();
    });

    self.isCoach = self.computed(function () {
        return !!coachedParticipantId() && hasCalls();
    });
    self.isScreenMonitor = self.computed(function () {
        return screenMonitoringTargetUserIds().length > 0;
    });

    self.isBargeIn = self.computed(function () {
        return self.isSupervisorBarged() && hasCalls();
    });

    self.canBargeIn = self.computed(function () {
        return (
            !isCurrentUserOnCall() &&
            ((!endTime() && hasCalls()) || hasConnectedCalls())
        );
    });

    self.isPermittedToBargeIn = self.computed(function () {
        return hasBargeInCallPermission() && hasCalls();
    });

    self.isBargedIn = self.computed(function () {
        return !!bargedInCall();
    });

    self.canMonitorMessage = self.computed(function () {
        return (
            !isCurrentUserOnCall() &&
            ((!endTime() && hasMessages()) || hasConnectedMessages())
        );
    });

    self.isPermittedToMonitorMessage = self.computed(function () {
        return hasMonitorMessagePermission() && hasMessages();
    });

    self.canMonitorScreen = self.computed(function () {
        return (
            !isCurrentUserOnCall() &&
            (hasConnectedCalls() ||
                hasConnectedMessages() ||
                hasConnectedEmails())
        );
    });

    self.isMonitoredScreen = self.computed(function () {
        return !!monitoredScreen();
    });

    self.isPermittedToStartOrStopScreenMonitoring = self.computed(function () {
        if (!self.isScreenMonitoringEnabled) {
            return false;
        }
        if (!(hasCalls() || hasMessages() || hasEmails())) {
            return false;
        }

        // If screen monitoring is not active, use conversation level permission (for starting)
        if (!self.isMonitoredScreen()) {
            return hasConversationLevelScreenMonitorPermission();
        }

        // If screen monitoring is active, check permission based on monitoring type
        if (screenMonitoringType() === ScreenMonitoringType.AGENT_LEVEL) {
            return hasAgentLevelScreenMonitorPermission();
        } else if (
            screenMonitoringType() === ScreenMonitoringType.CONVERSATION_LEVEL
        ) {
            return hasConversationLevelScreenMonitorPermission();
        } else {
            return false;
        }
    });

    self.isMonitoredMessage = self.computed(function () {
        return !!monitoredMessage();
    });

    self.isMonitorMessage = self.computed(function () {
        return !!monitoredParticipantId() && hasMessages();
    });

    self.isActive = self.computed(function () {
        return !endTime();
    });
    self.isMachine = function () {
        if (participant.purpose) {
            const participantPurpose = participant.purpose.toLowerCase();
            return (
                participantPurpose === "acd" ||
                participantPurpose === "ivr" ||
                participantPurpose === "dialer.system" ||
                participantPurpose === "voicemail" ||
                participantPurpose === "fax"
            );
        }
        return false;
    };
    self.isInternal = function () {
        if (participant.purpose) {
            return (
                (participant.purpose.toLowerCase() === "user" ||
                    participant.purpose.toLowerCase() === "agent") &&
                !self.isMachine()
            );
        } else if (
            participant.participantType &&
            participant.participantType.toLowerCase() === "internal"
        ) {
            // workaround for https://inindca.atlassian.net/browse/AR-2731
            return !!participant.userId;
        }
        return false;
    };

    self.isExternal = function () {
        if (participant.purpose) {
            return (
                (participant.purpose.toLowerCase() === "external" ||
                    participant.purpose.toLowerCase() === "customer") &&
                !self.isMachine()
            );
        } else if (
            participant.participantType &&
            participant.participantType.toLowerCase() === "external"
        ) {
            // workaround for https://inindca.atlassian.net/browse/AR-2731
            return true;
        }
        return false;
    };

    // Computed observables for action visibility
    self.canShowMonitor = self.computed(function () {
        return (
            (self.canMonitor() && self.isPermittedToMonitor()) ||
            self.isMonitored()
        );
    });

    self.canShowCoach = self.computed(function () {
        return (
            self.isInternal() &&
            ((self.canCoach() && self.isPermittedToCoach()) || self.isCoached())
        );
    });

    self.canShowBargeIn = self.computed(function () {
        return (
            self.isInternal() &&
            ((self.canBargeIn() && self.isPermittedToBargeIn()) ||
                self.isBargedIn())
        );
    });

    self.canShowMonitorMessage = self.computed(function () {
        return (
            (self.canMonitorMessage() && self.isPermittedToMonitorMessage()) ||
            self.isMonitoredMessage()
        );
    });

    self.canShowMonitorScreen = self.computed(function () {
        return (
            self.isInternal() &&
            ((self.canMonitorScreen() &&
                self.isPermittedToStartOrStopScreenMonitoring()) ||
                self.isMonitoredScreen())
        );
    });

    self.showActionButton = self.computed(function () {
        return (
            (self.isPermittedToMonitor() ||
                self.isPermittedToCoach() ||
                self.isPermittedToBargeIn() ||
                self.isPermittedToMonitorMessage() ||
                self.isPermittedToStartOrStopScreenMonitoring()) &&
            self.isActive() &&
            self.isInternal() &&
            (hasConnectedCalls() ||
                hasConnectedMessages() ||
                hasConnectedEmails()) &&
            !self.bargedParticipantId()
        );
    });

    self.messageActionButton = ko.computed(function () {
        if (self.isMonitoredMessage()) {
            return {
                label: ko.i18n.renderString("conversationDetails.stopMonitor"),
                accent: "tertiary",
            };
        }
        return {
            label: ko.i18n.renderString("conversationDetails.monitor"),
            accent: "primary",
        };
    });

    self.actionButton = ko.computed(function () {
        if (self.isMonitored()) {
            return {
                label: ko.i18n.renderString("conversationDetails.stopMonitor"),
                accent: "tertiary",
            };
        } else if (self.isCoached()) {
            return {
                label: ko.i18n.renderString("conversationDetails.stopCoach"),
                accent: "tertiary",
            };
        } else if (self.isBargedIn()) {
            return {
                label: ko.i18n.renderString("conversationDetails.stopBarging"),
                accent: "tertiary",
            };
        } else if (
            !self.isPermittedToCoach() &&
            !self.isPermittedToMonitor() &&
            self.isPermittedToBargeIn()
        ) {
            return {
                label: ko.i18n.renderString("conversationDetails.bargeIn"),
                accent: "primary",
            };
        } else if (
            !self.isPermittedToMonitor() &&
            self.isAlreadyCoached() &&
            self.isPermittedToBargeIn()
        ) {
            return {
                label: ko.i18n.renderString("conversationDetails.bargeIn"),
                accent: "primary",
            };
        } else if (!self.isPermittedToMonitor()) {
            return {
                label: ko.i18n.renderString("conversationDetails.coach"),
                accent: "primary",
            };
        }
        return {
            label: ko.i18n.renderString("conversationDetails.monitor"),
            accent: "primary",
        };
    });

    self.screenMonitorActionButton = ko.computed(function () {
        if (!self.isScreenMonitoringEnabled) {
            return null;
        }

        if (self.isMonitoredScreen()) {
            return {
                label: ko.i18n.renderString(
                    "conversationDetails.stopMonitorScreen"
                ),
                accent: "tertiary",
                icon: Icons.STOP_SCREEN_MONITOR,
            };
        }
        return {
            label: ko.i18n.renderString("conversationDetails.monitorScreen"),
            accent: "primary",
            icon: Icons.SCREEN_MONITOR,
        };
    });

    /**
     * ACTION BUTTON SYSTEM
     *
     * Conversation supervision actions (Monitor, Coach, Barge-In, Screen Monitor)
     * with priority-based primary button and dropdown menu.
     *
     * FLOW:
     * 1. getActionStateFlags() - Collects all state (active states, permissions, context)
     * 2. get*Actions() helpers - Generate action objects based on state (one per action type)
     * 3. getAllAvailableActions() - Combines all actions, sorts by priority
     * 4. primaryActionButton() - First enabled action becomes primary button
     * 5. dropdownActions() - Remaining actions appear in dropdown
     *
     * PRIORITY: Stop actions (1-10) > Start actions (11-20)
     * - Stop actions never disabled, start actions may be disabled by permissions/state
     * - Lower number = higher priority = appears first
     *
     * ACTION STRUCTURE:
     * { type, label, accent, priority, disabled, click, iconName }
     *
     * ADDING NEW ACTION (e.g., "whisper"):
     * 1. Add icons to src/enums/Icons.js (WHISPER, STOP_WHISPER)
     * 2. Add priorities to ACTION_PRIORITY (use next available: 5 for stop, 15 for start)
     * 3. Add icon mapping in getActionIconName()
     * 4. Add state flags to getActionStateFlags()
     * 5. Create getWhisperActions(state) helper (follow getMonitorActions pattern)
     * 6. Add to getAllAvailableActions(): ...getWhisperActions(state),
     * 7. Implement view model methods: whisper(), stopWhisper(), isWhispered(), isPermittedToWhisper()
     * 8. Add i18n strings to src/strings/en-US.json
     *
     * BUSINESS RULES:
     * - Coach/Barge-In: call conversations only
     * - Screen Monitor: all conversation types
     * - Barge-in active → disables monitor/coach
     * - Already monitored/coached → may disable actions
     *
     */

    // Helper function to get icon name based on action type and feature toggles
    self.getActionIconName = function (actionType) {
        switch (actionType) {
            case "monitor":
                return Icons.MONITOR;
            case "stopMonitor":
                return Icons.STOP_MONITOR;
            case "coach":
                return Icons.COACH;
            case "stopCoach":
                return Icons.STOP_COACH;
            case "bargeIn":
                return Icons.BARGE_IN;
            case "stopBargeIn":
                return Icons.STOP_BARGE_IN;
            case "screenMonitor":
                return Icons.SCREEN_MONITOR;
            case "stopScreenMonitor":
                return Icons.STOP_SCREEN_MONITOR;
            default:
                return "";
        }
    };

    // Action priority constants - stop actions have higher priority (lower numbers)
    // Gap between stop and start actions allows insertion without renumbering existing code
    const ACTION_PRIORITY = {
        // Stop actions  - grouped together
        STOP_MONITOR: 1,
        STOP_COACH: 2,
        STOP_BARGE_IN: 3,
        STOP_SCREEN_MONITOR: 4,
        // Gap for new stop actions: 5-10

        // Start actions - grouped together
        MONITOR: 11,
        COACH: 12,
        BARGE_IN: 13,
        SCREEN_MONITOR: 14,
        // Gap for new start actions: 15-20
    };

    // Helper function to get all state flags
    function getActionStateFlags() {
        return {
            isMonitored: self.isMonitored() || self.isMonitoredMessage(),
            isCoached: self.isCoached(),
            isBargedIn: self.isBargedIn(),
            isMonitoredScreen: self.isMonitoredScreen(),
            canCoach:
                self.isPermittedToCoach() && !self.coachRequestInProgress(),
            canMonitor:
                (self.isPermittedToMonitor() ||
                    self.isPermittedToMonitorMessage()) &&
                !self.monitorRequestInProgress(),
            canBargeIn:
                self.isPermittedToBargeIn() && !self.bargeInRequestInProgress(),
            canMonitorScreen:
                self.isPermittedToStartOrStopScreenMonitoring() &&
                !self.screenMonitorRequestInProgress(),
            isAlreadyCoached: self.isAlreadyCoached(),
            isAlreadyMonitored: self.isAlreadyMonitored(),
            isCallConv: self.isCallConversation(),
            isMessageConv: self.isMessageConversation(),
        };
    }

    // Helper function to create monitor actions
    function getMonitorActions(state) {
        const actions = [];
        const {
            isMonitored,
            canMonitor,
            isBargedIn,
            isAlreadyMonitored,
            isCallConv,
            isMessageConv,
        } = state;

        if (isMonitored) {
            const stopLabel = isCallConv
                ? ko.i18n.renderString("conversationDetails.stopMonitorCall")
                : ko.i18n.renderString(
                      "conversationDetails.stopMonitorMessage"
                  );
            actions.push({
                type: "stopMonitor",
                label: stopLabel,
                accent: "tertiary",
                priority: ACTION_PRIORITY.STOP_MONITOR,
                disabled: false,
                click: () => self.stopMonitor(),
                iconName: self.getActionIconName("stopMonitor"),
                canShowAction:
                    self.canShowMonitor() || self.canShowMonitorMessage(),
                requestInProgress: self.monitorRequestInProgress,
            });
        } else if (isCallConv || isMessageConv) {
            const monitorLabel = isCallConv
                ? ko.i18n.renderString("conversationDetails.monitorCall")
                : ko.i18n.renderString("conversationDetails.monitorMessage");
            actions.push({
                type: "monitor",
                label: monitorLabel,
                accent: "primary",
                priority: ACTION_PRIORITY.MONITOR,
                disabled:
                    !canMonitor ||
                    isBargedIn ||
                    (isAlreadyMonitored && isMessageConv),
                click: () => self.monitor(),
                iconName: self.getActionIconName("monitor"),
                canShowAction:
                    self.canShowMonitor() || self.canShowMonitorMessage(),
                requestInProgress: self.monitorRequestInProgress,
            });
        }

        return actions;
    }

    // Helper function to create coach actions
    function getCoachActions(state) {
        const actions = [];
        const {
            isCoached,
            canCoach,
            isAlreadyCoached,
            isBargedIn,
            isCallConv,
        } = state;

        if (isCoached) {
            actions.push({
                type: "stopCoach",
                label: ko.i18n.renderString("conversationDetails.stopCoach.v2"),
                accent: "tertiary",
                priority: ACTION_PRIORITY.STOP_COACH,
                disabled: false,
                click: () => self.stopCoach(),
                iconName: self.getActionIconName("stopCoach"),
                canShowAction: self.canShowCoach(),
                requestInProgress: self.coachRequestInProgress,
            });
        } else if (isCallConv) {
            actions.push({
                type: "coach",
                label: ko.i18n.renderString("conversationDetails.coach"),
                accent: "primary",
                priority: ACTION_PRIORITY.COACH,
                disabled: !canCoach || isAlreadyCoached || isBargedIn,
                click: () => self.coach(),
                iconName: self.getActionIconName("coach"),
                canShowAction: self.canShowCoach(),
                requestInProgress: self.coachRequestInProgress,
            });
        }

        return actions;
    }

    // Helper function to create barge-in actions
    function getBargeInActions(state) {
        const actions = [];
        const { isBargedIn, canBargeIn, isCallConv } = state;

        if (isBargedIn) {
            actions.push({
                type: "stopBargeIn",
                label: ko.i18n.renderString(
                    "conversationDetails.stopBarging.v2"
                ),
                accent: "tertiary",
                priority: ACTION_PRIORITY.STOP_BARGE_IN,
                disabled: false,
                click: () => self.stopBarging(),
                iconName: self.getActionIconName("stopBargeIn"),
                canShowAction: self.canShowBargeIn(),
                requestInProgress: self.bargeInRequestInProgress,
            });
        } else if (isCallConv) {
            actions.push({
                type: "bargeIn",
                label: ko.i18n.renderString("conversationDetails.bargeIn.v2"),
                accent: "primary",
                priority: ACTION_PRIORITY.BARGE_IN,
                disabled: !canBargeIn,
                click: () => self.bargeIn(),
                iconName: self.getActionIconName("bargeIn"),
                canShowAction: self.canShowBargeIn(),
                requestInProgress: self.bargeInRequestInProgress,
            });
        }

        return actions;
    }

    // Helper function to create screen monitor actions
    function getScreenMonitorActions(state) {
        const actions = [];
        const { isMonitoredScreen, canMonitorScreen } = state;

        if (isMonitoredScreen) {
            actions.push({
                type: "stopScreenMonitor",
                label: ko.i18n.renderString(
                    "conversationDetails.stopMonitorScreen"
                ),
                accent: "tertiary",
                priority: ACTION_PRIORITY.STOP_SCREEN_MONITOR,
                disabled: false,
                click: () => self.stopScreenMonitor(),
                iconName: self.getActionIconName("stopScreenMonitor"),
                canShowAction: self.canShowMonitorScreen(),
                requestInProgress: self.screenMonitorRequestInProgress,
            });
        } else {
            actions.push({
                type: "screenMonitor",
                label: ko.i18n.renderString(
                    "conversationDetails.monitorScreen"
                ),
                accent: "primary",
                priority: ACTION_PRIORITY.SCREEN_MONITOR,
                disabled: !canMonitorScreen,
                click: () => self.startScreenMonitor(),
                iconName: self.getActionIconName("screenMonitor"),
                canShowAction: self.canShowMonitorScreen(),
                requestInProgress: self.screenMonitorRequestInProgress,
            });
        }

        return actions;
    }

    // Centralized action definitions with priority and conditions
    self.getAllAvailableActions = ko.computed(function () {
        if (!self.isScreenMonitoringEnabled) {
            return [];
        }

        const state = getActionStateFlags();
        const actions = [
            ...getMonitorActions(state),
            ...getCoachActions(state),
            ...getBargeInActions(state),
            ...getScreenMonitorActions(state),
        ];

        return actions.sort((a, b) => a.priority - b.priority);
    });

    // Primary action button (highest priority enabled action)
    self.primaryActionButton = ko.computed(function () {
        if (!self.isScreenMonitoringEnabled) {
            return null;
        }

        const actions = self.getAllAvailableActions();
        // Find the first enabled action (not disabled)
        const enabledAction = actions.find((action) => !action.disabled);

        if (enabledAction) {
            return {
                label: enabledAction.label,
                accent: enabledAction.accent,
                icon: enabledAction.iconName,
            };
        }

        // Default fallback
        const defaultLabel = self.isCallConversation()
            ? ko.i18n.renderString("conversationDetails.monitorCall")
            : ko.i18n.renderString("conversationDetails.monitorMessage");
        return {
            label: defaultLabel,
            accent: "primary",
            icon: self.getActionIconName("monitor"),
        };
    });

    // Click handler for primary action button - matches by label and calls the action
    // Look up the action at click time to avoid race conditions with state changes
    self.onClickPrimaryActionButton = ko.computed(function () {
        return function () {
            if (!self.isScreenMonitoringEnabled) {
                return;
            }

            const actions = self.getAllAvailableActions();
            const buttonState = self.primaryActionButton();
            // Find the action that matches the current button label at click time
            const matchingAction = actions.find(
                (action) => action.label === buttonState.label
            );

            if (matchingAction) {
                matchingAction.click();
            } else {
                // Fallback to monitor if no matching action found (shouldn't happen in normal flow)
                self.monitor();
            }
        };
    });

    // Simplified dropdown actions - all actions except the primary one
    self.dropdownActions = ko.computed(function () {
        if (!self.isScreenMonitoringEnabled) {
            return [];
        }

        const allActions = self.getAllAvailableActions();
        const primaryAction = self.primaryActionButton();

        // Return all actions except the primary one, plus any parallel actions
        return allActions
            .filter((action) => {
                // Include if it's not the primary action
                if (action.label !== primaryAction.label) {
                    return true;
                }
            })
            .map((action) => ({
                type: action.type,
                label: action.label,
                disabled: action.disabled, // Use the disabled state from action definition
                click: action.click,
                iconName: action.iconName,
            }));
    });

    self.hasParticipantName = function () {
        return participant.name && participant.name.trim().length > 0;
    };

    // Set this when there is a user for the participant and the participant name is not the user name.
    self.userDisplayString = self.computed(function () {
        if (self.user && self.user() && self.user().name) {
            if (self.nameDisplayString() !== self.user().name()) {
                return self.user().name();
            }
        }
    });

    self.hasWrapUpCode = self.computed(function () {
        return !!conversationParticipant()?.wrapup;
    });

    self.hasName = ko.observable(true);

    self.wrapUpCodeName = self.computed(function () {
        let name;
        if (self.hasWrapUpCode()) {
            name = getWrapupName(conversationParticipant()?.wrapup);
            self.hasName(!!name);
        }
        return name;
    });

    self.hasWrapUpNotes = self.computed(function () {
        return !!conversationParticipant()?.wrapup?.notes;
    });

    self.wrapUpNotes = self.computed(function () {
        if (self.hasWrapUpNotes()) {
            return conversationParticipant()?.wrapup?.notes;
        } else {
            return "";
        }
    });

    self.awaitingWrapup = self.computed(function () {
        return (
            !self.hasWrapUpCode() && conversationParticipant()?.wrapupRequired
        );
    });

    self.pastWrapUpCodesView = {
        popoverTitle: self.computed(function () {
            return ko.i18n.renderString({
                name: "conversationDetails.pastWrapUps.title",
                data: {
                    participantName: self.userDisplayString() || self.name,
                },
            });
        }),
        pastWrapUpCodes: self.computed(function () {
            const pastWrapUpCodes = [];
            if (analyticsParticipant() && analyticsParticipant().sessions) {
                _.each(analyticsParticipant().sessions, function (session) {
                    if (
                        session &&
                        session.segments &&
                        session.mediaType !== "callback"
                    ) {
                        _.each(session.segments, function (segment) {
                            if (
                                segment.segmentType === "wrapup" &&
                                segment.wrapUpCode
                            ) {
                                if (!wrapUpCodeNamesById[segment.wrapUpCode]) {
                                    wrapUpCodeNamesById[segment.wrapUpCode] =
                                        ko.observable();

                                    if (
                                        uuidUtils.isValidUuid(
                                            segment.wrapUpCode
                                        ) &&
                                        qualityAuth.canViewWrapupCode(
                                            ko.unwrap(
                                                self.conversationDivisionIds
                                            )
                                        )
                                    ) {
                                        wrapUpCodeService
                                            .getWrapupCodeById(
                                                segment.wrapUpCode
                                            )
                                            .then(function (wrapUpFetchResult) {
                                                wrapUpCodeNamesById[
                                                    segment.wrapUpCode
                                                ](
                                                    getWrapupName(
                                                        wrapUpFetchResult
                                                    )
                                                );
                                            })
                                            .catch(function (err) {
                                                LOG.error(
                                                    "Error fetching wrap up code with id",
                                                    {
                                                        wrapUpCode:
                                                            segment.wrapUpCode,
                                                        err,
                                                    }
                                                );
                                            });
                                    } else if (
                                        _.startsWith(
                                            segment.wrapUpCode,
                                            "ININ-"
                                        )
                                    ) {
                                        wrapUpCodeNamesById[segment.wrapUpCode](
                                            segment.wrapUpCode
                                        );
                                    } else {
                                        LOG.debug(
                                            "Skipping invalid wrap-up code format"
                                        );
                                    }
                                }

                                const wrapUpNote = ko.observable(
                                    segment.wrapUpNote
                                );
                                pastWrapUpCodes.push({
                                    wrapUpCodeName:
                                        wrapUpCodeNamesById[segment.wrapUpCode],
                                    wrapUpCodeId: segment.wrapUpCode,
                                    wrapUpNote: wrapUpNote,
                                    hasWrapUpCodeName: ko.computed(function () {
                                        return !!wrapUpCodeNamesById[
                                            segment.wrapUpCode
                                        ]();
                                    }),
                                    hasWrapUpNotes: ko.computed(function () {
                                        return !!wrapUpNote();
                                    }),
                                });
                            }
                        });
                    }
                });
            }
            return pastWrapUpCodes;
        }),
    };

    self.hasAdditionalWrapUpCodes = ko.computed(function () {
        return (
            self.pastWrapUpCodesView.pastWrapUpCodes() &&
            self.pastWrapUpCodesView.pastWrapUpCodes().length > 1
        );
    });

    self.toggleAdditionalWrapUpCodes = function (id) {
        const popoverTargetId = "popover-AdditionalWrapUpCodes-wrapper-" + id;
        const spanTargetId = "popover-AdditionalWrapUpCodes-" + id;
        const popoverTarget = document.getElementById(popoverTargetId);
        const spanTarget = document.getElementById(spanTargetId);
        const isOpen = popoverTarget.isOpen ? false : true;
        spanTarget.ariaExpanded = isOpen;
        popoverTarget.isOpen = isOpen;
    };

    self.monitor = function () {
        self.monitorRequestInProgress(true);
        let response = null;
        if (hasCalls()) {
            response = callService.monitorCall(
                self.conversationId,
                participant.id,
                true
            );
        } else if (hasMessages()) {
            response = messageService.monitorMessage(
                self.conversationId,
                participant.id,
                true
            );
        }
        return response
            .catch(
                errorCallbackFactory.createCallback({
                    namespace: "conversationDetails.monitorRequest",
                })
            )
            .finally(function () {
                self.monitorRequestInProgress(false);
            });
    };

    self.stopMonitor = function () {
        if (monitoredCall() || monitoredMessage()) {
            self.monitorRequestInProgress(true);
            let response = null;
            if (hasCalls()) {
                response = callService.endCall(
                    monitoredCall().id,
                    monitoredCall().participantForCurrentUser().id
                );
            } else if (hasMessages()) {
                response = messageService.endMessage(
                    monitoredMessage().id,
                    monitoredMessage().participantForCurrentUser().id
                );
            }
            return response
                .catch(
                    errorCallbackFactory.createCallback({
                        namespace: "conversationDetails.stopMonitorRequest",
                    })
                )
                .finally(function () {
                    self.monitorRequestInProgress(false);
                });
        }
    };

    self.showActionButtonForSingleParticipantView = self.computed(function () {
        return (
            self.canShowMonitor() ||
            self.canShowCoach() ||
            self.canShowBargeIn() ||
            (self.canShowMonitorMessage() &&
                self.isInternal() &&
                self.isActive() &&
                hasConnectedMessages()) ||
            self.canShowMonitorScreen()
        );
    });

    self.subscribe(self.isMonitored, function () {
        if (self.isInternal()) {
            isAnyInternalParticipantMonitored(self.isMonitored());
        } else if (self.isExternal()) {
            isExternalParticipantMonitored(self.isMonitored());
        }
        self.isOpen(false);
    });

    self.subscribe(self.isMonitoredMessage, function () {
        if (self.isInternal()) {
            isAnyInternalParticipantMonitored(self.isMonitoredMessage());
        }
        self.isOpen(false);
    });

    self.subscribe(self.isMonitoredScreen, function () {
        if (self.isInternal()) {
            isAnyInternalParticipantMonitored(self.isMonitoredScreen());
        }
        self.isOpen(false);
    });

    self.coach = function () {
        self.coachRequestInProgress(true);
        return callService
            .coachCall(self.conversationId, participant.id, true)
            .catch(
                errorCallbackFactory.createCallback({
                    namespace: "conversationDetails.coachRequest",
                })
            )
            .finally(function () {
                self.coachRequestInProgress(false);
            });
    };

    self.stopCoach = function () {
        if (coachedCall()) {
            self.coachRequestInProgress(true);
            return callService
                .endCall(
                    coachedCall().id,
                    coachedCall().participantForCurrentUser().id
                )
                .catch(
                    errorCallbackFactory.createCallback({
                        namespace: "conversationDetails.stopCoachRequest",
                    })
                )
                .finally(function () {
                    self.coachRequestInProgress(false);
                });
        }
    };

    self.startScreenMonitor = function () {
        // Check WEM-437 feature toggle
        if (!self.isScreenMonitoringEnabled) {
            return;
        }

        self.screenMonitorRequestInProgress(true);
        return screenMonitoringService
            .startScreenMonitoring(self.conversationId, participant.id)
            .catch(
                errorCallbackFactory.createCallback({
                    namespace: "screenMonitorRequest.error",
                })
            )
            .finally(function () {
                self.screenMonitorRequestInProgress(false);
            });
    };

    self.stopScreenMonitor = function () {
        // Check WEM-437 feature toggle
        if (!self.isScreenMonitoringEnabled) {
            return;
        }

        if (monitoredScreen()) {
            const supervisorParticipant =
                monitoredScreen().participantForCurrentUser();
            self.screenMonitorRequestInProgress(true);
            const promise =
                screenMonitoringType() === ScreenMonitoringType.AGENT_LEVEL
                    ? screenMonitoringService.stopAgentLevelScreenMonitoring(
                          participant.userId,
                          screenMonitoringId()
                      )
                    : screenMonitoringService.stopConversationLevelScreenMonitoring(
                          self.conversationId,
                          supervisorParticipant?.id,
                          screenMonitoringId()
                      );

            return promise
                .catch(
                    errorCallbackFactory.createCallback({
                        namespace: "screenMonitorRequest.error",
                    })
                )
                .finally(function () {
                    self.screenMonitorRequestInProgress(false);
                });
        }
    };

    self.bargeIn = function () {
        self.bargeInRequestInProgress(true);
        return callService
            .bargeInCall(self.conversationId, participant.id, true)
            .catch(
                errorCallbackFactory.createCallback({
                    namespace: "conversationDetails.bargeInRequest",
                })
            )
            .finally(function () {
                self.bargeInRequestInProgress(false);
            });
    };

    self.stopBarging = function () {
        if (bargedInCall()) {
            self.bargeInRequestInProgress(true);
            return callService
                .endCall(
                    bargedInCall().id,
                    bargedInCall().participantForCurrentUser().id
                )
                .catch(
                    errorCallbackFactory.createCallback({
                        namespace: "conversationDetails.stopBargingRequest",
                    })
                )
                .finally(function () {
                    self.bargeInRequestInProgress(false);
                });
        }
    };

    self.onClickActionButton = function () {
        if (self.isMonitored()) {
            self.stopMonitor();
        } else if (self.isCoached()) {
            self.stopCoach();
        } else if (self.isBargedIn()) {
            self.stopBarging();
        } else if (
            !self.isPermittedToCoach() &&
            !self.isPermittedToMonitor() &&
            self.isPermittedToBargeIn()
        ) {
            self.bargeIn();
        } else if (
            !self.isPermittedToMonitor() &&
            self.isAlreadyCoached() &&
            self.isPermittedToBargeIn()
        ) {
            self.bargeIn();
        } else if (!self.isPermittedToMonitor()) {
            self.coach();
        } else {
            self.monitor();
        }
    };

    self.messageOnClickActionButton = function () {
        if (self.isMonitoredMessage()) {
            self.stopMonitor();
        } else {
            self.monitor();
        }
    };

    self.screenMonitorOnClickActionButton = function () {
        if (self.isMonitoredScreen()) {
            self.stopScreenMonitor();
        } else {
            self.startScreenMonitor();
        }
    };

    self.subscribe(self.isBargedIn, function () {
        isAnyInternalParticipantBargedIn(self.isBargedIn());
        self.isOpen(false);
    });

    self.subscribe(self.isCoached, function () {
        isAnyInternalParticipantCoached(self.isCoached());
        self.isOpen(false);
    });

    self.disableActionButton = self.computed(function () {
        return (
            !self.isMonitored() &&
            !self.isCoached() &&
            !self.isBargedIn() &&
            isCurrentUserOnCall()
        );
    });

    self.disableActionDropdown = self.computed(function () {
        if (!self.isScreenMonitoringEnabled) {
            return false;
        }

        const isCallConv = self.isCallConversation();
        const isMessageConv = self.isMessageConversation();

        // If there's already an active monitoring session, don't disable dropdown
        if (
            self.isMonitored() ||
            self.isCoached() ||
            self.isBargedIn() ||
            self.isMonitoredMessage() ||
            self.isMonitoredScreen()
        ) {
            return false;
        } else if (isCurrentUserOnCall()) {
            return true;
        }

        // For call conversation: disable if all 4 permissions are not there
        if (isCallConv) {
            const hasMonitorPermission = self.isPermittedToMonitor();
            const hasCoachPermission = self.isPermittedToCoach();
            const hasBargeInPermission = self.isPermittedToBargeIn();
            const hasScreenMonitorPermission =
                self.isPermittedToStartOrStopScreenMonitoring();

            // If only coach permission is there and isAlreadyCoached is true, disable dropdown
            if (
                hasCoachPermission &&
                !hasMonitorPermission &&
                !hasBargeInPermission &&
                !hasScreenMonitorPermission &&
                self.isAlreadyCoached()
            ) {
                return true;
            }
        }

        // For message conversation: disable if both permissions are not there
        if (isMessageConv) {
            const hasMonitorMessagePermission =
                self.isPermittedToMonitorMessage();
            const hasScreenMonitorPermission =
                self.isPermittedToStartOrStopScreenMonitoring();

            // If only monitor permission is there and isAlreadyMonitored is true, disable dropdown
            if (
                hasMonitorMessagePermission &&
                !hasScreenMonitorPermission &&
                self.isAlreadyMonitored()
            ) {
                return true;
            }
        }

        return false;
    });

    self.hideMessageActionButton = self.computed(function () {
        return !self.isMonitoredMessage() && isCurrentUserOnCall();
    });

    self.hideScreenMonitorActionButton = self.computed(function () {
        return !self.isMonitoredScreen() && isCurrentUserOnCall();
    });

    self.nameDisplayString = self.computed(function () {
        let calculatedDisplayString = "";
        if (self.isExternal()) {
            if (self.externalContact()) {
                if (
                    self.externalContact().firstName &&
                    self.externalContact().lastName
                ) {
                    calculatedDisplayString = ko.i18n.renderString({
                        name: "conversationDetails.externalContact.nameFormat",
                        data: {
                            firstName: self.externalContact().firstName,
                            lastName: self.externalContact().lastName,
                        },
                    });
                } else if (
                    self.externalContact().firstName ||
                    self.externalContact().lastName
                ) {
                    calculatedDisplayString =
                        self.externalContact().firstName ||
                        self.externalContact().lastName;
                }
            }
        }
        return calculatedDisplayString || self.name;
    });

    self.getMessageType = self.computed(function () {
        return (
            participant.messages &&
            participant.messages.length &&
            participant.messages[0].type
        );
    });

    const formatAddress = function (rawValue) {
        if (rawValue.indexOf("sip:") === 0) {
            return ko.i18n.renderString(
                "conversationDetails.participantAddress.sipDevice"
            );
        } else {
            return ko.i18n.formatPhoneAddress(rawValue);
        }
    };

    const analyticsAddressDisplayString = self.computed(function () {
        let calculatedDisplayString = null;
        if (analyticsParticipant()) {
            const lastTelephonySession = _.findLast(
                analyticsParticipant().sessions,
                function (session) {
                    return (
                        (session.sessionDnis || session.dnis) &&
                        session.ani &&
                        session.direction
                    );
                }
            );
            if (lastTelephonySession) {
                let address = null;
                const lastDirection =
                    lastTelephonySession.direction.toLowerCase();
                if (self.isInternal()) {
                    if (lastDirection.toLowerCase() === "inbound") {
                        address = lastTelephonySession.sessionDnis
                            ? lastTelephonySession.sessionDnis
                            : lastTelephonySession.dnis;
                    } else {
                        address = lastTelephonySession.ani;
                    }
                } else if (self.isExternal()) {
                    if (lastDirection.toLowerCase() === "inbound") {
                        address = lastTelephonySession.ani;
                    } else {
                        address = lastTelephonySession.sessionDnis
                            ? lastTelephonySession.sessionDnis
                            : lastTelephonySession.dnis;
                    }
                }

                if (address) {
                    calculatedDisplayString = formatAddress(address);
                }
            }
        }
        return calculatedDisplayString;
    });

    self.addressDisplayString = self.computed(function () {
        // Try to calculate the display from the direct analytics response.
        let calculatedDisplayString = analyticsAddressDisplayString();

        // Fallback to finding the display from the merged conversation endpoint response.
        if (!calculatedDisplayString) {
            calculatedDisplayString = self.address;
            if (participant.messages && participant.messages.length > 0) {
                if (self.getMessageType() === MESSAGE_SUBTYPE_SMS) {
                    const smsMessageWithAddress = _.find(
                        participant.messages,
                        "fromAddress"
                    );
                    if (smsMessageWithAddress) {
                        const fromAddress =
                            smsMessageWithAddress.fromAddress.addressNormalized;
                        calculatedDisplayString =
                            ko.i18n.formatPhoneAddress(fromAddress);
                    }
                } else {
                    calculatedDisplayString = "";
                    const messageWithAddress = _.find(
                        participant.messages,
                        "fromAddress"
                    );
                    if (
                        messageWithAddress &&
                        messageWithAddress.fromAddress.addressDisplayable
                    ) {
                        calculatedDisplayString =
                            messageWithAddress.fromAddress.addressDisplayable;
                    }
                }
            } else if (participant.calls && participant.calls.length > 0) {
                if (
                    participant.address &&
                    participant.address.indexOf("sip:") === 0
                ) {
                    calculatedDisplayString = ko.i18n.renderString(
                        "conversationDetails.participantAddress.sipDevice"
                    );
                } else {
                    if (self.isInternal()) {
                        calculatedDisplayString =
                            direction.toLowerCase() === "inbound"
                                ? self.dnis
                                : self.ani;
                    } else if (self.isExternal()) {
                        calculatedDisplayString =
                            direction.toLowerCase() === "inbound"
                                ? self.ani
                                : self.dnis;
                    }
                }
            } else if (participant.chats && participant.chats.length > 0) {
                // Show nothing instead of the room jid that's in the address
                calculatedDisplayString = "";
            }
        }

        return calculatedDisplayString;
    });
    if (
        self.conversationId &&
        participant.participantType &&
        participant.participantType.toLowerCase() === "internal"
    ) {
        self.empathyScore = self.computed(function () {
            const empathyScoreString = ko.observable("- -");
            const empathyScoreIcon = ko.observable();
            const empathyScoreTooltip = ko.observable(
                "conversationDetails.overallAgentEmpathies.empty"
            );
            if (self.conversationMetricsErr()) {
                errorCallbackFactory.createCallback({
                    namespace: "conversationDetails.empathy",
                    logError: false, // log in the supplied callback function instead
                    strategies: {
                        404: function () {
                            LOG.info("Overall empathy analytics not found.");
                        },
                        DEFAULT: function () {
                            LOG.error(
                                "Error fetching empathy analysis for conversation",
                                {
                                    conversation: self.conversationId,
                                    err: self.conversationMetricsErr(),
                                }
                            );
                        },
                    },
                })(self.conversationMetricsErr());
            } else if (self.conversationMetrics()) {
                // set empathy score
                let score;
                if (self.conversationMetrics().empathyScores) {
                    for (const empathyScore of self.conversationMetrics()
                        .empathyScores) {
                        if (empathyScore.userId === participant.userId) {
                            score = empathyScore.score;
                            break;
                        }
                    }
                    if (typeof score === "number") {
                        const empathyScore = Math.round(score);
                        if (empathyScore > 0) {
                            empathyScoreString("+" + empathyScore.toString());
                            empathyScoreIcon(
                                EmpathyResources.agentEmpathyScore.iconClassName
                            );
                            empathyScoreTooltip(
                                "conversationDetails.overallAgentEmpathies.empathyScore"
                            );
                        } else if (empathyScore < 0) {
                            empathyScoreString(empathyScore.toString());
                            empathyScoreIcon(
                                EmpathyResources.unhelpfulBehaviorScore
                                    .iconClassName
                            );
                            empathyScoreTooltip(
                                "conversationDetails.overallAgentEmpathies.empathyScore"
                            );
                        } else {
                            empathyScoreString(empathyScore.toString());
                            if (
                                featureToggleService.isEnabled(
                                    FeatureToggle.WEM_1418_IDUI_SPARK_V4_FLARE
                                )
                            ) {
                                empathyScoreIcon(
                                    EmpathyResources.neutralEmpathyScore
                                        .iconName
                                );
                            } else {
                                empathyScoreIcon(
                                    EmpathyResources.neutralEmpathyScore
                                        .iconClassName
                                );
                            }
                            empathyScoreTooltip(
                                "conversationDetails.overallAgentEmpathies.neutralEmpathyScore"
                            );
                        }
                    }
                }
            }
            return {
                empathyScoreString: empathyScoreString,
                empathyScoreIcon: empathyScoreIcon,
                empathyScoreTooltip: empathyScoreTooltip,
            };
        });
    }

    self.extraAddressDisplayInformation = self.computed(function () {
        if (
            participant.calls &&
            participant.calls.length > 0 &&
            participant.address &&
            participant.address.indexOf("sip:") === 0
        ) {
            return self.address;
        }
        return "";
    });

    self.displayString = self.computed(function () {
        return self.nameDisplayString() || self.addressDisplayString();
    });

    self.isAddressCopyable = self.computed(function () {
        return (
            self.addressDisplayString() &&
            !self.extraAddressDisplayInformation()
        );
    });

    self.isAddressCopyableSentiment = self.computed(function () {
        return (
            self.addressDisplayString() &&
            self.addressDisplayString() !==
                ko.i18n.renderString(
                    "conversationDetails.participantAddress.sipDevice"
                )
        );
    });

    self.displayParticipantName = function () {
        if (self.isExternal()) {
            if (self.nameDisplayString()) {
                return self.nameDisplayString();
            } else {
                return ko.i18n.renderString("playback.guestParticipant");
            }
        } else {
            if (self.userDisplayString()) {
                return self.userDisplayString();
            } else if (self.hasParticipantName()) {
                return participant.name;
            } else {
                return ko.i18n.renderString("playback.unknownParticipant");
            }
        }
    };
});

const ConversationDetailsView = ViewModel.extend(function (
    conversationObservable,
    conversationDetailsV6,
    options,
    shouldDisplayEmailRecordingInfo,
    coachedParticipants,
    monitoredParticipants
) {
    const self = this;
    options = _.assign({ expandUser: true }, options);

    directionStrings = {
        INBOUND: ko.i18n.renderString("interactionsList.direction.inbound"),
        OUTBOUND: ko.i18n.renderString("interactionsList.direction.outbound"),
        BOTH: ko.i18n.renderString(
            "interactionsList.direction.inboundAndOutbound"
        ),
    };
    // Expose Icons to templates for use in HTML bindings
    self.Icons = Icons;
    self.isAlreadyCoached = ko.observable(false);
    self.isAlreadyMonitored = ko.observable(false);
    self.isAnyInternalParticipantMonitored = ko.observable(false);
    self.isAnyInternalParticipantCoached = ko.observable(false);
    self.isAnyInternalParticipantBargedIn = ko.observable(false);
    self.isExternalParticipantMonitored = ko.observable(false);
    self.startDateTime = ko.observable();
    self.endDateTime = ko.observable();
    self.duration = ko.observable();
    self.participantViews = ko.observableArray();
    self.participantAttributesPresent = ko.observable(false);
    self.agentName = ko.observable();
    self.externalName = ko.observable();
    self.agentNames = ko.observableArray();
    self.activeAgentNames = ko.observableArray();
    self.direction = ko.observable();
    self.active = ko.observable(false);
    self.hasCalls = ko.observable(false);
    self.isAcdVoicemail = ko.observable(false);
    self.hasMessages = ko.observable(false);
    self.hasChats = ko.observable(false);
    self.hasEmails = ko.observable(false);
    self.hasScreenMonitorings = ko.observable(false);
    self.hasCallbacks = ko.observable(false);
    self.hasCobrowseSessions = ko.observable(false);
    self.hasVideos = ko.observable(false);
    self.hasScreenshares = ko.observable(false);
    self.hasRecordings = ko.observable(false);
    self.internalParticipantViews = ko.observableArray();
    self.externalParticipantViews = ko.observableArray();
    self.queueArray = ko.observableArray([]);
    self.queue = ko.observable();
    self.bothDirections = ko.observable(false);
    self.wrapUpCode = ko.observable();
    self.wrapUpCodeId = ko.observable();
    self.downloading = ko.observable(false);
    self.recordingInformation = ko.observableArray([]);
    self.today = moment().toISOString();
    self.engagementSources = ko.observableArray([]);
    self.conversationId = self.computed(function () {
        return ko.isObservable(conversationObservable) &&
            conversationObservable() &&
            conversationObservable().id
            ? conversationObservable().id
            : options.conversationId
            ? options.conversationId
            : null;
    });
    self.analyticsRetentionDays = ko.observable();
    self.internalUsers = ko.observableArray();
    self.externalContacts = ko.observableArray();

    self.isScreenMonitoringEnabled = featureToggleService.isEnabled(
        FeatureToggle.WEM_437_SCREEN_MONITORING
    );

    self.isOptimizeAPICallsInIDUIEnabled = featureToggleService.isEnabled(
        FeatureToggle.REC_6867_OPTIMIZE_API_CALLS_IN_IDUI
    );

    // Computed property to determine conversation types from all participants
    // Returns an array of types since multiple types can be present
    self.conversationType = self.computed(function () {
        const conversation = ko.unwrap(conversationObservable);
        if (!conversation || !conversation.participants) {
            return [];
        }
        const participants = ko.unwrap(conversation.participants);
        if (participants.length === 0) {
            return [];
        }

        // Return empty array if screen monitoring feature toggle is disabled
        if (!self.isScreenMonitoringEnabled) {
            return [];
        }

        const types = new Set();
        participants.forEach((participant) => {
            if (participantHasCalls(participant)) {
                types.add(ConversationType.CALL);
            }
            if (participantHasMessages(participant)) {
                types.add(ConversationType.MESSAGE);
            }
            if (participantHasEmails(participant)) {
                types.add(ConversationType.EMAIL);
            }
            if (participantHasScreenMonitorings(participant)) {
                types.add(ConversationType.SCREEN_MONITORING);
            }
        });
        return Array.from(types);
    });

    self.updatingRetention = ko.observable(false);
    self.canEditRetention = qualityAuth.canEditRetention();
    self.canEditSnippetRecordingRetention =
        qualityAuth.canEditSnippetRecordingRetention();
    self.canEditScreenRecordingRetention =
        qualityAuth.canEditScreenRecordingRetention();
    self.canApplyProtectionStatus = ko.observable(
        qualityAuth.canApplyProtectionStatus()
    );
    self.canRevokeProtectionStatus = ko.observable(
        qualityAuth.canRevokeProtectionStatus()
    );
    self.protectionStatus = ko.observable();
    self.lastSavedProtectionStatus = ko.observable();
    self.isEditingRetention = ko.observable(false);
    const timeFormatWithSeconds = ko.i18n.renderString(
        "interactionsList.timeFormatSeconds"
    );
    self.formattedStartDateTime = ko.observable();
    self.formattedEndDateTime = ko.observable();
    self.dnis = ko.observable("");
    self.ani = ko.observable("");
    self.messageType = ko.observable();
    self.siptracesView = self.subView(
        SiptracesView,
        self.hasCalls,
        conversationObservable
    );
    self.agentEmpathy = ko.observable();
    self.multipleAgentEmpathies = ko.observable(false);
    self.categoriesView = self.subView(
        CategoriesView,
        self.conversationId,
        featureToggleService.isEnabled(FeatureToggle.STA_CATEGORY)
    );
    self.overallCustomerSentiments = ko.observable();
    self.conversationAcousticDurationPercentages = ko.observableArray();
    self.percentGraphView = self.subView(
        PercentGraphView,
        self.conversationAcousticDurationPercentages
    );
    self.maxAcousticDurationPercentage = ko.observable();
    self.shouldDisplayEmailRecordingInfo = ko.observable();
    self.conversationMetrics = ko.observable(null);
    self.conversationMetricsErr = ko.observable(null);
    self.localizedDirection = ko.computed(function () {
        if (self.direction()) {
            return getLocalizedDirection(self.direction());
        }
        return "";
    });

    self.formatPercentage = function (percent) {
        return ko.i18n.formatNumber(percent / 100, {
            style: "percent",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    /**
     * @returns {string} A comma-separated list of localized engagement sources
     */
    self.localizedEngagementSourceValues = self.computed(function () {
        if (!self.engagementSources || self.engagementSources().length === 0) {
            return "";
        }
        return self
            .engagementSources()
            .map((engagementSource) =>
                ko.i18n.renderString(
                    `analytics.openMessaging.engagementSource.${engagementSource}`
                )
            )
            .join(", ");
    });

    /**
     * Returns an array of unique interaction IDs associated with the conversation.
     * It includes from the associated conversation and the consultation conversations.
     * @returns {string[]} An array of interaction IDs.
     */
    self.associatedInteractionIds = ko.computed(function () {
        if (
            !featureToggleService.isEnabled(
                FeatureToggle.PURE_7349_ACD_CONFERENCE_MS3_PHASE_1
            )
        ) {
            return [];
        }

        const interactionIds = new Set();
        const conversation = ko.toJS(conversationObservable);

        // Check if conversation is aged (no id means API returned 404 after 1.5 years)
        const isAgedConversation = conversation && !conversation.id;

        if (!isAgedConversation) {
            // Use conversation API data for non-aged conversations
            if (conversation?.associatedConversation?.id) {
                interactionIds.add(conversation.associatedConversation.id);
            }

            conversation?.consultationConversations?.forEach((c) => {
                if (c?.id) {
                    interactionIds.add(c.id);
                }
            });
        } else {
            // Fall back to recordings API data only for aged conversations
            const recordings = ko.unwrap(options?.recordingInformation) || [];
            recordings.forEach((recording) => {
                if (
                    recording?.associatedConversationIds &&
                    Array.isArray(recording.associatedConversationIds)
                ) {
                    recording.associatedConversationIds.forEach((id) => {
                        if (id) {
                            interactionIds.add(id);
                        }
                    });
                }
            });
        }

        return Array.from(interactionIds);
    });

    const isConversationActive = function (conversation) {
        return (
            !conversation.endTime &&
            !!conversation.id &&
            !!conversation.startTime
        );
    };

    // Sort by wrap-up state
    self.sortedParticipantViewsWithWrapup = self.computed(function () {
        return self.participantViews().sort(function (left, right) {
            if (left.awaitingWrapup() && right.hasWrapUpCode()) {
                return 1;
            }
            if (left.hasWrapUpCode() && right.awaitingWrapup()) {
                return -1;
            }
            return 0;
        });
    });

    const setDirection = function (direction) {
        if (_.isUndefined(self.direction())) {
            self.direction(direction);
        } else if (direction === "inbound" && self.direction() === "outbound") {
            self.direction("inbound / outbound");
            self.bothDirections(true);
        } else if (direction === "outbound" && self.direction() === "inbound") {
            self.direction("inbound / outbound");
            self.bothDirections(true);
        }
    };

    const addQueueName = function (name) {
        if (_.isUndefined(self.queue())) {
            self.queue(name);
        } else {
            self.queue(
                self.queue() +
                    ko.i18n.renderString("interaction.list.delimiter") +
                    name
            );
        }
    };

    const addWrapupName = function (name) {
        if (_.isUndefined(self.wrapUpCode())) {
            self.wrapUpCode(name);
        } else {
            self.wrapUpCode(
                self.wrapUpCode() +
                    ko.i18n.renderString("interaction.list.delimiter") +
                    name
            );
        }
    };

    /**
     * Gets all unique engagement sources from messages in the conversation and sets them on the view model.
     * @param {Conversation} conversation - The conversation data
     */
    const setEngagementSources = function (conversation) {
        const engagementSourcesSet = new Set();
        if (_.isArray(conversation?.participants)) {
            conversation.participants.forEach((participant) => {
                if (participant?.messages) {
                    participant.messages.forEach((message) => {
                        if (
                            message?.engagementSource &&
                            message.engagementSource.trim() !== ""
                        ) {
                            engagementSourcesSet.add(message.engagementSource);
                        }
                    });
                }
            });
        }
        self.engagementSources(Array.from(engagementSourcesSet));
    };

    // For active conversations, calculate the duration from startTime until now.
    // A setInterval will call this function on each row to update
    self.updateDuration = function () {
        if (self.active()) {
            const elapsedTime = moment().diff(self.startDateTime());
            self.duration(elapsedTime);
        }
    };

    self.displayWrapupInfo = function () {
        const conversation = ko.toJS(conversationObservable);
        if (!conversation) {
            return false;
        }
        return _.some(
            ko.toJS(conversationObservable).participants,
            function (participant) {
                const participantView = _.find(
                    self.participantViews(),
                    function (view) {
                        return view.participantId === participant.id;
                    }
                );

                return (
                    participantView &&
                    participantView.isInternal() &&
                    (participantView.hasWrapUpCode() ||
                        participantView.hasWrapUpNotes())
                );
            }
        );
    };

    self.awaitingAnyWrapups = function () {
        const conversation = ko.toJS(conversationObservable);
        if (!conversation) {
            return false;
        }
        return _.some(
            ko.toJS(conversationObservable).participants,
            function (participant) {
                const participantView = _.find(
                    self.participantViews(),
                    function (view) {
                        return view.participantId === participant.id;
                    }
                );
                return participantView && participantView.awaitingWrapup();
            }
        );
    };

    /**
     * A helper function to generate the hash path for given interaction ID.
     * @param {string} interactionId
     * @param {boolean} openInSameAnalyticsTab - Whether to open the interaction in the same analytics tab or a new tab
     * @returns {string} The hash path for the interaction ID
     */
    self.hashPathForInteractionId = function (
        interactionId,
        openInSameAnalyticsTab = true
    ) {
        if (!interactionId) {
            return "#";
        }
        const queryParams = {
            shouldOpenInSameTab: !!openInSameAnalyticsTab,
            internal: false,
        };
        return setQueryParams(
            navHelper.buildInteractionHash("admin", interactionId, ""),
            queryParams
        );
    };

    self.navigateToAssociatedInteraction = function (interactionId) {
        if (interactionId) {
            navHelper.navigateTo(
                self.hashPathForInteractionId(interactionId, false)
            );
        }
    };

    self.getAssociatedInteractionUrl = function (interactionId) {
        if (!interactionId) {
            return "#";
        }
        const hashPath = self.hashPathForInteractionId(interactionId, false);
        return navHelper.asHostUrl(hashPath);
    };

    const isCurrentUserOnCall = self.computed(function () {
        const conversation = ko.unwrap(conversationObservable);
        if (
            !userService.currentUser() ||
            !conversation ||
            !conversation.id ||
            !isConversationActive(conversation)
        ) {
            return false;
        }

        const participants = ko.unwrap(conversation.participants);
        const activeParticipantForUser = _.find(
            participants,
            function (participant) {
                return (
                    participant.userId === userService.currentUser().id &&
                    !participant.endTime
                );
            }
        );

        return !!activeParticipantForUser;
    });

    self.canDownloadRecording = self.computed(function () {
        // Can download if the feature to enforce is off, or if they have the permission.
        const conversation = ko.unwrap(conversationObservable);
        if (!conversation || !conversation.divisionIds) {
            return false;
        }
        return qualityAuth.canDownloadRecording(conversation.divisionIds());
    });

    self.canDownloadSnippetRecording = self.computed(function () {
        const conversation = ko.unwrap(conversationObservable);
        if (!conversation || !conversation.divisionIds) {
            return false;
        }
        return qualityAuth.canDownloadSnippetRecording(
            conversation.divisionIds()
        );
    });

    self.isDeletionProtectionStatusModified = self.computed(function () {
        return self.lastSavedProtectionStatus() !== self.protectionStatus();
    });

    self.toggleProtectionStatus = function (obj, event) {
        self.protectionStatus(event.target.checked);
    };

    self.isUpdatingProtectionStatusDisabled = self.computed(function () {
        return self.lastSavedProtectionStatus()
            ? !self.canRevokeProtectionStatus()
            : !self.canApplyProtectionStatus();
    });

    self.cancelEditRetention = function () {
        self.protectionStatus(self.lastSavedProtectionStatus());
        self.isEditingRetention(false);
    };

    self.updateDeletionProtectionOption = function () {
        self.updatingRetention(true);

        let protectionStatusPromise = Promise.resolve();
        if (self.lastSavedProtectionStatus() !== self.protectionStatus()) {
            protectionStatusPromise = self.updateProtectionStatus().then(() => {
                self.lastSavedProtectionStatus(self.protectionStatus());
                self.isEditingRetention(false);
            });
        } else {
            self.isEditingRetention(false);
        }

        Promise.resolve(protectionStatusPromise).finally(() => {
            self.updatingRetention(false);
        });
    };

    self.updateProtectionStatus = function () {
        if (
            (self.canApplyProtectionStatus() &&
                !self.lastSavedProtectionStatus()) ||
            (self.canRevokeProtectionStatus() &&
                self.lastSavedProtectionStatus())
        ) {
            return conversationsProtectionService
                .updateProtectionStatus(
                    self.conversationId(),
                    self.protectionStatus()
                )
                .catch(function (error) {
                    self.protectionStatus(self.lastSavedProtectionStatus());
                    errorCallbackFactory.createCallback({
                        namespace:
                            "conversationDetails.updateProtectionStatus.error.default",
                    })(error);
                })
                .then(self.getProtectionStatus);
        }
    };

    self.getProtectionStatus = function () {
        return conversationsProtectionService
            .getProtectionStatus(self.conversationId())
            .then(function (response) {
                if (response.length > 0) {
                    self.protectionStatus(true);
                    self.lastSavedProtectionStatus(true);
                } else {
                    self.protectionStatus(false);
                    self.lastSavedProtectionStatus(false);
                }
            })
            .catch(function (error) {
                self.protectionStatus(self.lastSavedProtectionStatus());
                errorCallbackFactory.createCallback({
                    namespace:
                        "conversationDetails.getProtectionStatus.error.default",
                })(error);
            });
    };

    self.beginEditRetention = function () {
        self.isEditingRetention(true);
    };

    const hasMonitorCallPermission = self.computed(function () {
        const conversation = ko.unwrap(conversationObservable);
        if (!conversation || !conversation.id || !conversation.divisionIds) {
            return true;
        }
        return qualityAuth.canMonitorCall(conversation.divisionIds());
    });

    const hasCoachCallPermission = self.computed(function () {
        const conversation = ko.unwrap(conversationObservable);
        if (!conversation || !conversation.id || !conversation.divisionIds) {
            return true;
        }
        return qualityAuth.canCoachCall(conversation.divisionIds());
    });

    const hasBargeInCallPermission = self.computed(function () {
        const conversation = ko.unwrap(conversationObservable);
        if (!conversation || !conversation.id || !conversation.divisionIds) {
            return true;
        }
        return qualityAuth.canBargeInCall(conversation.divisionIds());
    });

    const hasMonitorMessagePermission = self.computed(function () {
        const conversation = ko.unwrap(conversationObservable);
        if (!conversation || !conversation.id || !conversation.divisionIds) {
            return true;
        }
        return qualityAuth.canMonitorMessaging(conversation.divisionIds());
    });

    const hasAgentLevelScreenMonitorPermission = self.computed(function () {
        const conversation = ko.unwrap(conversationObservable);
        if (!conversation || !conversation.id || !conversation.divisionIds) {
            return true;
        }
        const divisionIds = conversation.divisionIds();
        return qualityAuth.canScreenMonitorAgentLevel(divisionIds);
    });

    const hasConversationLevelScreenMonitorPermission = self.computed(
        function () {
            const conversation = ko.unwrap(conversationObservable);
            if (
                !conversation ||
                !conversation.id ||
                !conversation.divisionIds
            ) {
                return true;
            }
            const divisionIds = conversation.divisionIds();
            return qualityAuth.canScreenMonitorConversationLevel(divisionIds);
        }
    );

    const recordingOptionsToInformation = function (newRecs) {
        if (newRecs.length > 0) {
            const returnObject = [];
            const recordingIds = {};
            const uniqueRecordingIds = [];
            _.each(options.recordingInformation(), function (recording) {
                const recordingId = segmentUtils.getOriginalRecordingId(
                    recording.recordingId
                );
                if (recordingId in recordingIds) {
                    recordingIds[recordingId] += 1;
                } else {
                    recordingIds[recordingId] = 1;
                    uniqueRecordingIds.push(recordingId);
                }
                const recordingObject = segmentUtils.isSegmentedId(
                    recording.recordingId
                )
                    ? createRecordingObject(
                          recording,
                          _.findIndex(
                              uniqueRecordingIds,
                              (e) => e === recordingId
                          ) + 1,
                          recordingIds[recordingId]
                      )
                    : createRecordingObject(
                          recording,
                          _.findIndex(
                              uniqueRecordingIds,
                              (e) => e === recordingId
                          ) + 1
                      );
                if (!_.isEmpty(recordingObject)) {
                    returnObject.push(recordingObject);
                }
            });
            self.recordingInformation(returnObject);
            if (self.isOptimizeAPICallsInIDUIEnabled) {
                self.getProtectionStatus();
            }
        } else {
            self.recordingInformation([]);
            self.hasRecordings(false);
        }
    };

    self.initRecording = self.computed(function () {
        if (
            ko.isObservable(options.recordingInformation) &&
            options.recordingInformation() &&
            options.recordingInformation().length > 0
        ) {
            if (self.hasEmails()) {
                self.hasRecordings(true);
                self.getProtectionStatus();
                if (
                    shouldDisplayEmailRecordingInfo &&
                    shouldDisplayEmailRecordingInfo() === true
                ) {
                    self.shouldDisplayEmailRecordingInfo(true);
                } else {
                    self.shouldDisplayEmailRecordingInfo(false);
                }
            } else {
                recordingOptionsToInformation(options.recordingInformation());
                self.subscribe(
                    options.recordingInformation,
                    recordingOptionsToInformation
                );
                _.debounce(function () {
                    self.initRecording.dispose();
                })();
            }
            if (qualityAuth.canViewAnalyticsDataretentionSettings()) {
                analyticsService
                    .getAnalyticsDataRetention()
                    .then(function (response) {
                        self.analyticsRetentionDays(response.retentionDays);
                    })
                    .catch(function (err) {
                        if (err.details && err.details.status !== 404) {
                            LOG.error(
                                "Error getting analytics retention value",
                                err
                            );
                        }
                    });
            }
        }
    });

    function pollFunction(callback, recordingObject) {
        (function poll() {
            setTimeout(function () {
                callback()
                    .then(function (recordingModel) {
                        if (!recordingModel) {
                            poll();
                        } else {
                            recordingObject.downloading(false);
                            const link = document.getElementsByClassName(
                                "conversation-download-recording"
                            );
                            link[0].href = recordingModel.mediaUris.S.mediaUri;
                            link[0].click();
                        }
                    })
                    .catch(function (err) {
                        recordingObject.downloading(false);
                        errorCallbackFactory.createCallback({
                            namespace: "playback.downloadRecording",
                        })(err);
                    });
            }, 3000);
        })();
    }

    function getMediaSubjectName(recording) {
        let mediaSubjectName;
        if (recording.mediaSubtype === "Trunk") {
            mediaSubjectName =
                recording.mediaSubject ||
                ko.i18n.renderString(
                    "conversationDetails.recordingInformation.mediaSubject.name.guest"
                );
        } else if (
            recording.mediaSubtype === "Consult" ||
            recording.mediaSubtype === "Station" ||
            recording.mediaSubtype === "Snippet"
        ) {
            if (recording.mediaSubject) {
                const user = recording.users?.find(
                    (user) => user.id === recording.mediaSubject
                );
                if (user) {
                    mediaSubjectName = user.name;
                } else {
                    mediaSubjectName = ko.i18n.renderString(
                        "conversationDetails.recordingInformation.mediaSubject.name.unknown"
                    );
                    LOG.warn(
                        `Failed to retrieve MediaSubject user with id: ${recording.mediaSubject}`
                    );
                }
            } else if (
                recording.mediaSubtype === "Station" ||
                recording.mediaSubtype === "Snippet"
            ) {
                mediaSubjectName = ko.i18n.renderString(
                    "conversationDetails.recordingInformation.mediaSubject.name.noName"
                );
                LOG.warn(
                    `No MediaSubject data available for ${recording.mediaSubtype} recording`
                );
            } else {
                mediaSubjectName = ko.i18n.renderString(
                    "conversationDetails.recordingInformation.mediaSubject.name.unknown"
                );
                LOG.warn("No MediaSubject data available for recording");
            }
        }

        return mediaSubjectName;
    }
    function createRecordingObject(recording, recordingNumber, segmentNumber) {
        const recordingObject = {};

        if (recording.isAcdVoicemail) {
            self.hasRecordings(
                self.hasRecordings() || !ko.unwrap(recording.isDeleted)
            );
            return recording;
        }

        recordingObject.isRecordingSegment = segmentUtils.isSegmentedId(
            recording.recordingId
        );
        recordingObject.displayToolTipInHTML = ko.observable(
            "<div>" +
                ko.i18n.renderString(
                    "conversationDetails.retention.update.toolTip"
                ) +
                ' <a href="' +
                ko.i18n.renderString(
                    "conversationDetails.retention.update.toolTip.document.url"
                ) +
                '" target="_blank" rel="noopener noreferrer">' +
                ko.i18n.renderString(
                    "conversationDetails.retention.update.toolTip.document"
                ) +
                "</a></div>"
        );

        recordingObject.downloading = ko.observable(false);
        recordingObject.displayed = recording.isDisplayed;
        recordingObject.media = ko.i18n.renderString(
            `conversationDetails.recordingInformation.mediaType.${recording.media}`
        );
        recordingObject.mediaUris = recording.mediaUris;

        recordingObject.download = function (data) {
            recordingObject.downloading(true);
            const callbackFunction = function () {
                return qualityManagementService.getDownloadRecording(
                    self.conversationId(),
                    data?.recordingId,
                    data?.downloadFormatId,
                    data?.selectedFileFormat(),
                    data?.downloadFileName()
                );
            };
            pollFunction(callbackFunction, recordingObject);
        };

        recordingObject.editingDownload = ko.observable(false);
        recordingObject.downloadFileName = ko.observable();
        recordingObject.editDownload = function () {
            recordingObject.editingDownload(true);
        };
        recordingObject.cancelDownloadEdit = function () {
            recordingObject.editingDownload(false);
        };

        recordingObject.downloadFormatId = "formatId";

        if (recording.media === "email") {
            recordingObject.fileFormatOptions = ko.observable([
                {
                    text: ko.i18n.renderString("conversationDetails.eml"),
                    value: "eml",
                },
            ]);
            recordingObject.downloadFormatId = "emailFormatId";
        } else if (recording.media === "chat") {
            recordingObject.fileFormatOptions = ko.observable(["zip"]);
            recordingObject.downloadFormatId = "chatFormatId";
        } else if (recording.media === "message") {
            recordingObject.fileFormatOptions = ko.observable(["zip"]);
            recordingObject.downloadFormatId = "messageFormatId";
        } else {
            recordingObject.fileFormatOptions = ko.observable([
                {
                    text: ko.i18n.renderString("conversationDetails.oggOpus"),
                    value: "ogg_opus",
                },
                {
                    text: ko.i18n.renderString("conversationDetails.mp3"),
                    value: "mp3",
                },
                {
                    text: ko.i18n.renderString("conversationDetails.wav"),
                    value: "wav",
                },
                {
                    text: ko.i18n.renderString("conversationDetails.webm"),
                    value: "webm",
                },
            ]);

            recordingObject.downloadFormatId = "formatId";
        }

        recordingObject.selectedFileFormat = ko.observable();
        const isSparkV4FTEnabled = featureToggleService.isEnabled(
            FeatureToggle.WEM_1128_IDUI_SPARK_V4
        );
        if (
            recordingObject.fileFormatOptions().length === 1 ||
            (isSparkV4FTEnabled &&
                recordingObject.fileFormatOptions().length > 0)
        ) {
            if (recording.media === "chat" || recording.media === "message") {
                recordingObject.selectedFileFormat(
                    recordingObject.fileFormatOptions()[0]
                );
            } else {
                recordingObject.selectedFileFormat(
                    recordingObject.fileFormatOptions()[0]?.value
                );
            }
        }

        if (recording.media === "chat" || recording.media === "message") {
            recordingObject.downloadFileName(
                ko.i18n.renderString("conversationDetails.download.fileName", {
                    recordingMedia: recordingObject.media,
                    interactionId: self.conversationId(),
                })
            );
        } else if (recording.media === "email" || recording.media === "audio") {
            recordingObject.downloadFileName(
                ko.i18n.renderString("conversationDetails.download.fileName", {
                    recordingMedia: recordingObject.media,
                    recordingNumber: recordingNumber,
                    interactionId: self.conversationId(),
                })
            );
        }

        if (recording.mediaSubtype) {
            recordingObject.mediaSubtype = ko.i18n.renderString(
                `conversationDetails.recordingInformation.mediaSubtype.${recording.mediaSubtype}`
            );
        }
        recordingObject.mediaSubject = recording.mediaSubject;

        recordingObject.mediaSubjectName = getMediaSubjectName(recording);

        if (recording.media === "audio") {
            if (segmentNumber === undefined) {
                recordingObject.name = ko.i18n.renderString(
                    "conversationDetails.recordingInformation.name.audio",
                    {
                        recordingMedia: recordingObject.media,
                        recordingNumber: recordingNumber,
                    }
                );
            } else {
                recordingObject.name = ko.i18n.renderString(
                    "conversationDetails.recordingInformation.name.audio.segment",
                    {
                        recordingMedia: recordingObject.media,
                        recordingNumber: recordingNumber,
                        segmentNumber: segmentNumber,
                    }
                );
            }
        } else if (
            recording.media === "message" ||
            recording.media === "chat"
        ) {
            recordingObject.name = ko.i18n.renderString(
                "conversationDetails.recordingInformation.name.nonAudio",
                {
                    recordingMedia: recordingObject.media,
                }
            );
        } else if (recording.media === "email") {
            recordingObject.name = ko.i18n.renderString(
                "conversationDetails.recordingInformation.name.nonAudio",
                {
                    recordingMedia: recordingObject.media,
                    recordingNumber: recordingNumber,
                }
            );
        }

        recordingObject.recordingId = recording.recordingId;
        recordingObject.canDownload = ko.observable(
            (recording.recordingFileState === FileStateEnum.AVAILABLE ||
                recording.recordingFileState === FileStateEnum.RESTORED) &&
                ((recording.media === "audio" &&
                    !_.isUndefined(recording.mediaUris) &&
                    !_.isEmpty(recording.mediaUris)) ||
                    recording.media === "email" ||
                    recording.media === "chat" ||
                    recording.media === "message")
        );

        recordingObject.fileArchiveNotEditable =
            recording.recordingFileState === FileStateEnum.ARCHIVED ||
            recording.recordingFileState === FileStateEnum.RESTORING ||
            recording.recordingFileState === FileStateEnum.UPLOADING ||
            recording.recordingFileState === FileStateEnum.DELETED ||
            recording.recordingFileState === FileStateEnum.ARCHIVING;
        recordingObject.canUpdateRetention =
            recording.recordingFileState !== FileStateEnum.RESTORING &&
            recording.recordingFileState !== FileStateEnum.ARCHIVING &&
            recording.recordingFileState !== FileStateEnum.UPLOADING &&
            recording.recordingFileState !== FileStateEnum.DELETED;

        if (recording.media === "screen") {
            recordingObject.canDownloadRecording = self.canDownloadRecording;
            recordingObject.canEditRetention =
                self.canEditScreenRecordingRetention;
        } else if (
            recording.media === "audio" &&
            recording.mediaSubtype === "Snippet"
        ) {
            recordingObject.canDownloadRecording =
                self.canDownloadSnippetRecording;
            recordingObject.canEditRetention =
                self.canEditSnippetRecordingRetention;
        } else {
            recordingObject.canDownloadRecording = self.canDownloadRecording;
            recordingObject.canEditRetention = self.canEditRetention;
        }

        recordingObject.isDeleted = recording.isDeleted;
        self.hasRecordings(self.hasRecordings() || !recordingObject.isDeleted);

        //Setup defaults of nulls
        recording.retentionInformation = recording.retentionInformation || {};

        recordingObject.creationTime =
            recording.retentionInformation.creationTime;
        recordingObject.updateAllRetention = ko.observable(false);
        recordingObject.archiveDate = ko.observable();
        recordingObject.hasArchiveDuration = ko.observable();
        recordingObject.archiveDateDisplay = ko.observable();
        recordingObject.deleteDate = ko.observable();
        recordingObject.hasDeleteDuration = ko.observable();
        recordingObject.willBeDeletedSoon = ko.observable(false);
        recordingObject.deleteDateDisplay = ko.observable();
        // Shared view-level observables: changes via any recording object; affects all siblings.
        recordingObject.protectionStatus = self.isOptimizeAPICallsInIDUIEnabled
            ? self.protectionStatus
            : ko.observable();
        recordingObject.lastSavedProtectionStatus =
            self.isOptimizeAPICallsInIDUIEnabled
                ? self.lastSavedProtectionStatus
                : ko.observable();
        recordingObject.retentionLimitDate = ko.computed(function () {
            if (
                self.analyticsRetentionDays() == null ||
                recordingObject.creationTime == null
            ) {
                return null;
            }
            return moment
                .utc(recordingObject.creationTime)
                .local()
                .add(self.analyticsRetentionDays(), "days");
        });

        recordingObject.minDeleteDate = self.computed(function () {
            const archiveDate = recordingObject.archiveDate();
            if (archiveDate) {
                //calculate the min delete date
                const minDeleteDate = moment(archiveDate).add(1, "day");
                const today = moment();
                return moment.max(minDeleteDate, today).toISOString();
            }
            //if no archive date, set the min delete date to today
            return moment().toISOString();
        });

        recordingObject.maxDeleteDate = ko.computed(function () {
            if (recordingObject.updateAllRetention()) {
                const updatableRecordings = _.filter(
                    self.recordingInformation(),
                    function (recordingInformation) {
                        return (
                            includeInRetentionUpdate(recordingInformation) &&
                            recordingInformation.retentionLimitDate() !== null
                        );
                    }
                );
                const minRetentionLimitRecording = _.minBy(
                    updatableRecordings,
                    function (recordingInformation) {
                        return recordingInformation
                            .retentionLimitDate()
                            .valueOf();
                    }
                );
                return (
                    minRetentionLimitRecording &&
                    minRetentionLimitRecording.retentionLimitDate()
                );
            } else {
                return recordingObject.retentionLimitDate();
            }
        });

        recordingObject.maxDeleteDateDisplay = self.computed(function () {
            const maxDeleteDate = recordingObject.maxDeleteDate();
            if (maxDeleteDate) {
                return ko.i18n.formatDate(
                    new Date(maxDeleteDate.valueOf()),
                    "M/D/YYYY",
                    null,
                    SHORT_DATE_OPTIONS
                );
            }
            return "";
        });

        if (!self.isOptimizeAPICallsInIDUIEnabled) {
            recordingObject.getProtectionStatus = function () {
                return conversationsProtectionService
                    .getProtectionStatus(self.conversationId())
                    .then(function (response) {
                        if (response.length > 0) {
                            recordingObject.lastSavedProtectionStatus(true);
                            recordingObject.protectionStatus(true);
                        } else {
                            recordingObject.lastSavedProtectionStatus(false);
                            recordingObject.protectionStatus(false);
                        }
                    })
                    .catch(function (error) {
                        recordingObject.protectionStatus(
                            recordingObject.lastSavedProtectionStatus()
                        );
                        errorCallbackFactory.createCallback({
                            namespace:
                                "conversationDetails.getProtectionStatus.error.default",
                        })(error);
                    });
            };

            recordingObject.getProtectionStatus();

            recordingObject.updateProtectionStatus = function () {
                if (
                    (self.canApplyProtectionStatus() &&
                        !recordingObject.lastSavedProtectionStatus()) ||
                    (self.canRevokeProtectionStatus() &&
                        recordingObject.lastSavedProtectionStatus())
                ) {
                    return conversationsProtectionService
                        .updateProtectionStatus(
                            self.conversationId(),
                            recordingObject.protectionStatus()
                        )
                        .catch(function (error) {
                            recordingObject.protectionStatus(
                                recordingObject.lastSavedProtectionStatus()
                            );
                            errorCallbackFactory.createCallback({
                                namespace:
                                    "conversationDetails.updateProtectionStatus.error.default",
                            })(error);
                        })
                        .then(recordingObject.getProtectionStatus);
                }
            };
        }

        recordingObject.updateArchiveDate = function (archiveDate) {
            recordingObject.archiveDate(archiveDate || null);
            recordingObject.hasArchiveDuration(!!archiveDate);

            const archiveDateDisplay = recordingObject.archiveDate()
                ? ko.i18n.formatDate(
                      new Date(recordingObject.archiveDate()),
                      null,
                      null,
                      SHORT_DATE_OPTIONS
                  )
                : ko.i18n.renderString("conversationDetails.retention.no.date");

            recordingObject.archiveDateDisplay(archiveDateDisplay);
        };

        recordingObject.updateDeleteDate = function (deleteDate) {
            recordingObject.deleteDate(deleteDate || null);
            recordingObject.hasDeleteDuration(!!deleteDate);
            recordingObject.willBeDeletedSoon(
                recordingObject.deleteDate() &&
                    moment(recordingObject.deleteDate())
                        .startOf("day")
                        .isBefore(moment().add(1, "days").startOf("day"))
            );

            const deleteDateDisplay = recordingObject.deleteDate()
                ? ko.i18n.formatDate(
                      new Date(recordingObject.deleteDate()),
                      null,
                      null,
                      SHORT_DATE_OPTIONS
                  )
                : ko.i18n.renderString("conversationDetails.retention.no.date");

            recordingObject.deleteDateDisplay(deleteDateDisplay);
        };

        recordingObject.updateArchiveDate(
            recording.retentionInformation.archiveDate
        );
        recordingObject.lastSavedArchiveDate =
            recording.retentionInformation.archiveDate;
        recordingObject.updateDeleteDate(
            recording.retentionInformation.deleteDate
        );
        recordingObject.lastSavedDeleteDate =
            recording.retentionInformation.deleteDate;

        recordingObject.exportedDate = ko.observable(
            recording.retentionInformation.exportedDate
        );
        const exportedDateDisplay = recordingObject.exportedDate()
            ? ko.i18n.formatDate(
                  new Date(recordingObject.exportedDate()),
                  null,
                  null,
                  SHORT_DATE_OPTIONS
              )
            : ko.i18n.renderString("conversationDetails.retention.no.date");
        recordingObject.exportedDateDisplay =
            ko.observable(exportedDateDisplay);

        if (recordingObject.canEditRetention) {
            recordingObject.archiveDateEditor = self.subView(
                DateEditorView,
                recordingObject.archiveDate
            );
        }

        if (recordingObject.canEditRetention) {
            recordingObject.deleteDateEditor =
                recordingObject.deleteDateEditor ||
                self.subView(DateEditorView, recordingObject.deleteDate);
        }

        recordingObject.validateDates = self.computed(function () {
            if (recordingObject.archiveDate()) {
                if (
                    moment(recordingObject.deleteDate())
                        .startOf("day")
                        .subtract(1, "second")
                        .isBefore(moment(recordingObject.archiveDate()))
                ) {
                    recordingObject.deleteDate(
                        moment(recordingObject.archiveDate())
                            .add(1, "day")
                            .toISOString()
                    );
                }
            }
            if (
                recordingObject.maxDeleteDate() &&
                moment(recordingObject.deleteDate())
                    .startOf("day")
                    .isAfter(recordingObject.maxDeleteDate())
            ) {
                recordingObject.deleteDate(
                    moment(recordingObject.maxDeleteDate())
                        .utcOffset(moment().utcOffset(), true)
                        .startOf("day")
                        .toISOString()
                );
            }
        });

        recordingObject.isRetentionModified = self.computed(function () {
            return (
                (recordingObject.lastSavedArchiveDate || null) !==
                    recordingObject.archiveDate() ||
                (recordingObject.lastSavedDeleteDate || null) !==
                    recordingObject.deleteDate() ||
                recordingObject.lastSavedProtectionStatus() !==
                    recordingObject.protectionStatus()
            );
        });

        recordingObject.isEditingRetention = ko.observable(false);
        recordingObject.beginEditRetention = function () {
            recordingObject.isEditingRetention(true);
        };
        recordingObject.cancelEditRetention = function () {
            recordingObject.updateArchiveDate(
                recordingObject.lastSavedArchiveDate
            );
            recordingObject.updateDeleteDate(
                recordingObject.lastSavedDeleteDate
            );
            recordingObject.protectionStatus(
                recordingObject.lastSavedProtectionStatus()
            );
            recordingObject.isEditingRetention(false);
        };
        return recordingObject;
    }

    self.updateRetentionOption = function (recordingInformation) {
        self.updatingRetention(true);
        let recordingInformationList = [recordingInformation];
        if (recordingInformation.updateAllRetention()) {
            recordingInformationList = _.filter(
                self.recordingInformation(),
                includeInRetentionUpdate
            );
        }

        const updatePromises = _.map(
            recordingInformationList,
            function (recordingInformationToUpdate) {
                const payload = {
                    archiveDate:
                        recordingInformation.archiveDate() || undefined,
                    deleteDate: recordingInformation.deleteDate() || undefined,
                    recordingId: recordingInformationToUpdate.recordingId,
                    conversationId: self.conversationId(),
                };
                if (
                    payload.archiveDate ===
                        recordingInformationToUpdate.lastSavedArchiveDate &&
                    payload.deleteDate ===
                        recordingInformationToUpdate.lastSavedDeleteDate
                ) {
                    return new Promise.resolve();
                } else {
                    return qualityManagementService
                        .updateRecording(payload)
                        .then(function (res) {
                            recordingInformationToUpdate.lastSavedArchiveDate =
                                ko.unwrap(res.archiveDate);
                            recordingInformationToUpdate.lastSavedDeleteDate =
                                ko.unwrap(res.deleteDate);
                        })
                        .catch(function (error) {
                            errorCallbackFactory.createCallback({
                                namespace:
                                    "conversationDetails.updateRetentionDuration",
                            })(error);
                        })
                        .finally(function () {
                            recordingInformationToUpdate.archiveDate(undefined);
                            recordingInformationToUpdate.deleteDate(undefined);
                            recordingInformationToUpdate.updateArchiveDate(
                                recordingInformationToUpdate.lastSavedArchiveDate
                            );
                            recordingInformationToUpdate.updateDeleteDate(
                                recordingInformationToUpdate.lastSavedDeleteDate
                            );
                        });
                }
            }
        );

        let protectionStatusPromise = Promise.resolve();
        if (
            recordingInformation.lastSavedProtectionStatus() !==
            recordingInformation.protectionStatus()
        ) {
            if (self.isOptimizeAPICallsInIDUIEnabled) {
                protectionStatusPromise = self
                    .updateProtectionStatus()
                    .then(function () {
                        self.recordingInformation().forEach(function (
                            recordingObject
                        ) {
                            recordingObject.isEditingRetention(false);
                        });
                    });
            } else {
                protectionStatusPromise = recordingInformation
                    .updateProtectionStatus()
                    .then(function () {
                        self.recordingInformation().forEach(function (
                            recordingObject
                        ) {
                            recordingObject.lastSavedProtectionStatus(
                                recordingInformation.protectionStatus()
                            );
                            recordingObject.protectionStatus(
                                recordingInformation.protectionStatus()
                            );
                            recordingObject.isEditingRetention(false);
                        });
                    });
            }
        } else {
            recordingInformation.isEditingRetention(false);
        }

        Promise.all(updatePromises.concat([protectionStatusPromise])).finally(
            () => {
                self.updatingRetention(false);
            }
        );
    };

    const includeInRetentionUpdate = function (recordingObject) {
        return (
            !recordingObject.fileArchiveNotEditable ||
            recordingObject.isAcdVoicemail
        );
    };

    const isSparkV4FTEnabled = featureToggleService.isEnabled(
        FeatureToggle.WEM_1128_IDUI_SPARK_V4
    );

    // value property of gux-datepicker supports the dates only with english numbers and 'YYYY-MM-DD' format
    self.formattedGuxDatePickerValue = function (isoTimestamp) {
        const value = ko.unwrap(isoTimestamp);
        if (!isSparkV4FTEnabled || !value) {
            return "";
        }
        const CALENDAR_LOCALE = "en-us";
        const GUX_DATE_FORMAT = "YYYY-MM-DD";

        return moment(value).locale(CALENDAR_LOCALE).format(GUX_DATE_FORMAT);
    };

    self.onArchiveDateInput = function (recordingObject, event) {
        if (!isSparkV4FTEnabled || !event || !event.detail) {
            return;
        }
        const isoTimestamp = moment(event.detail).toISOString();
        recordingObject.updateArchiveDate(isoTimestamp);
    };

    self.onDeleteDateInput = function (recordingObject, event) {
        if (!isSparkV4FTEnabled || !event || !event.detail) {
            return;
        }
        const isoTimestamp = moment(event.detail).toISOString();
        recordingObject.updateDeleteDate(isoTimestamp);
    };

    self.removeArchiveDuration = function (recordingObject) {
        recordingObject.updateArchiveDate(null);
    };

    self.removeDeleteDuration = function (recordingObject) {
        recordingObject.updateDeleteDate(null);
    };

    self.addArchiveDuration = function (recordingObject) {
        recordingObject.updateArchiveDate(moment().toISOString());
    };

    self.addDeleteDuration = function (recordingObject) {
        const deleteDate = recordingObject.minDeleteDate();
        recordingObject.updateDeleteDate(deleteDate);
    };

    /**
     * END RETENTION DURATION SECTION
     */

    const getParticipantViewNames = (participantViews) => {
        // filter for uniqueness, then get the name
        return flow(
            uniqBy(({ userId }) => userId),
            map(
                ({ name, userDisplayString }) =>
                    userDisplayString() ||
                    name ||
                    ko.i18n.renderString("search.noName")
            )
        )(participantViews);
    };

    self.getEmpathyScore = function (metrics) {
        if (metrics.empathyScores) {
            if (
                metrics.empathyScores.length === 1 &&
                metrics.empathyScores[0] &&
                typeof metrics.empathyScores[0].score === "number"
            ) {
                return metrics.empathyScores[0].score;
            } else if (metrics.empathyScores.length > 1) {
                self.multipleAgentEmpathies(true);
            }
        }
        if (self.agentNames().length > 1) {
            self.multipleAgentEmpathies(true);
        }
        return undefined;
    };

    self.setEmpathyErrorFallback = function (err) {
        const empathyScoreString = ko.observable("- -");
        const empathyScoreIcon = ko.observable();
        const empathyScoreTooltip = ko.observable(
            "conversationDetails.overallAgentEmpathies.empty"
        );

        errorCallbackFactory.createCallback({
            namespace: "conversationDetails.empathy",
            logError: false, // log in the supplied callback function instead
            strategies: {
                403: function () {
                    LOG.info(
                        "Overall empathy analytics not available due to missing permissions."
                    );
                },
                404: function () {
                    LOG.info("Overall empathy analytics not found.");
                },
                DEFAULT: function () {
                    LOG.error(
                        "Error fetching empathy analysis for conversation",
                        {
                            conversation: self.conversationId(),
                            err,
                        }
                    );
                },
            },
        })(err);

        self.agentEmpathy({
            empathyScoreString: empathyScoreString,
            empathyScoreIcon: empathyScoreIcon,
            empathyScoreTooltip: empathyScoreTooltip,
        });
    };

    self.setEmpathy = function (conversationMetrics) {
        const empathyScoreString = ko.observable("- -");
        const empathyScoreIcon = ko.observable();
        const empathyScoreTooltip = ko.observable(
            "conversationDetails.overallAgentEmpathies.empty"
        );

        try {
            // set empathy score
            const score = self.getEmpathyScore(conversationMetrics);
            if (typeof score === "number") {
                const empathyScore = Math.round(score);
                if (empathyScore > 0) {
                    empathyScoreString("+" + empathyScore);
                    empathyScoreIcon(
                        EmpathyResources.agentEmpathyScore.iconClassName
                    );
                    empathyScoreTooltip(
                        "conversationDetails.overallAgentEmpathies.empathyScore"
                    );
                } else if (empathyScore < 0) {
                    empathyScoreString(empathyScore.toString());
                    empathyScoreIcon(
                        EmpathyResources.unhelpfulBehaviorScore.iconClassName
                    );
                    empathyScoreTooltip(
                        "conversationDetails.overallAgentEmpathies.empathyScore"
                    );
                } else {
                    empathyScoreString(empathyScore.toString());
                    if (
                        featureToggleService.isEnabled(
                            FeatureToggle.WEM_1418_IDUI_SPARK_V4_FLARE
                        )
                    ) {
                        empathyScoreIcon(
                            EmpathyResources.neutralEmpathyScore.iconName
                        );
                    } else {
                        empathyScoreIcon(
                            EmpathyResources.neutralEmpathyScore.iconClassName
                        );
                    }
                    empathyScoreTooltip(
                        "conversationDetails.overallAgentEmpathies.neutralEmpathyScore"
                    );
                }
            }
        } catch (err) {
            self.setEmpathyErrorFallback(err);
        } finally {
            self.agentEmpathy({
                empathyScoreString: empathyScoreString,
                empathyScoreIcon: empathyScoreIcon,
                empathyScoreTooltip: empathyScoreTooltip,
            });
        }
    };

    self.setOverallCustomerSentimentErrorFallback = function (err) {
        const sentimentScoreString = ko.observable();
        const sentimentScoreIcon = ko.observable();
        const sentimentScoreTooltip = ko.observable();
        const sentimentTrendString = ko.observable();
        const sentimentTrendIcon = ko.observable();
        const sentimentTrendTooltip = ko.observable();
        const sentimentTrendColor = ko.observable();
        const sentimentTrendPercentage = ko.observable();

        errorCallbackFactory.createCallback({
            logError: false, // log in the supplied callback function instead
            strategies: {
                403: function () {
                    LOG.info(
                        "Overall sentiment analytics not available due to permissions."
                    );
                },
                404: function () {
                    LOG.info("Overall sentiment analytics not found.");
                },
                DEFAULT: function () {
                    LOG.error(
                        "Error fetching sentiment analysis for conversation",
                        {
                            conversation: self.conversationId(),
                            err,
                        }
                    );
                },
            },
        })(err);

        sentimentScoreString("- -");
        sentimentTrendString("- -");

        if (
            featureToggleService.isEnabled(
                FeatureToggle.STA_SPLIT_TRANSCRIPT_VIEW_PERMISSION
            ) &&
            err.details?.status === 403
        ) {
            sentimentScoreTooltip(
                "conversationDetails.overallCustomerSentiments.noViewPermissionsScore"
            );
            sentimentTrendTooltip(
                "conversationDetails.overallCustomerSentiments.noViewPermissionsTrend"
            );
        } else {
            sentimentScoreTooltip(
                "conversationDetails.overallCustomerSentiments.emptyScore"
            );
            sentimentTrendTooltip(
                "conversationDetails.overallCustomerSentiments.emptyTrend"
            );
        }

        self.overallCustomerSentiments({
            sentimentScoreString: sentimentScoreString,
            sentimentScoreIcon: sentimentScoreIcon,
            sentimentScoreTooltip: sentimentScoreTooltip,
            sentimentTrendString: sentimentTrendString,
            sentimentTrendIcon: sentimentTrendIcon,
            sentimentTrendTooltip: sentimentTrendTooltip,
            sentimentTrendColor: sentimentTrendColor,
            sentimentTrendPercentage: sentimentTrendPercentage,
        });
    };

    self.setOverallCustomerSentiment = function (conversationMetrics) {
        const sentimentScoreString = ko.observable();
        const sentimentScoreIcon = ko.observable();
        const sentimentScoreTooltip = ko.observable();
        const sentimentTrendString = ko.observable();
        const sentimentTrendIcon = ko.observable();
        const sentimentTrendTooltip = ko.observable();
        const sentimentTrendColor = ko.observable();
        const sentimentTrendPercentage = ko.observable();

        try {
            // set sentiment score
            if (typeof conversationMetrics.sentimentScore === "number") {
                const sentimentScore = Math.round(
                    conversationMetrics.sentimentScore * 100
                );
                if (sentimentScore > 0) {
                    sentimentScoreString("+" + sentimentScore);
                    sentimentScoreIcon(
                        OverallSentimentResources.positiveSentimentScore
                            .iconClassName
                    );
                    sentimentScoreTooltip(
                        "conversationDetails.overallCustomerSentiments.sentimentScore"
                    );
                } else if (sentimentScore < 0) {
                    sentimentScoreString(sentimentScore.toString());
                    sentimentScoreIcon(
                        OverallSentimentResources.negativeSentimentScore
                            .iconClassName
                    );
                    sentimentScoreTooltip(
                        "conversationDetails.overallCustomerSentiments.sentimentScore"
                    );
                } else {
                    sentimentScoreString(sentimentScore.toString());
                    if (
                        featureToggleService.isEnabled(
                            FeatureToggle.WEM_1418_IDUI_SPARK_V4_FLARE
                        )
                    ) {
                        sentimentScoreIcon(
                            OverallSentimentResources.neutralSentimentScore
                                .iconName
                        );
                    } else {
                        sentimentScoreIcon(
                            OverallSentimentResources.neutralSentimentScore
                                .iconClassName
                        );
                    }
                    sentimentScoreTooltip(
                        "conversationDetails.overallCustomerSentiments.neutralSentimentScore"
                    );
                }
            } else {
                sentimentScoreString("- -");
                sentimentScoreTooltip(
                    "conversationDetails.overallCustomerSentiments.emptyScore"
                );
            }

            // set sentiment trend
            if (typeof conversationMetrics.sentimentTrend === "number") {
                const sentimentTrend = conversationMetrics.sentimentTrend;
                const sentimentTrendAbsolutePercentFormatted =
                    ko.i18n.formatNumber(
                        Math.abs(conversationMetrics.sentimentTrend * 100),
                        // previously was {style: percent} which has a default MFD of 0
                        // so we match it to maintain previous behaviour, effectively removing only %
                        { maximumFractionDigits: 0 }
                    );
                sentimentTrendPercentage(
                    sentimentTrendAbsolutePercentFormatted.toString()
                );
                // improving
                if (sentimentTrend > 0.55) {
                    sentimentTrendString(
                        ko.i18n.renderString(
                            "conversationDetails.overallCustomerSentiments.improvingSentimentTrend"
                        )
                    );
                    sentimentTrendColor("sentiment-trend-improving");
                    sentimentTrendIcon(
                        OverallSentimentResources.improvingSentimentTrend
                            .iconClassName
                    );
                    sentimentTrendTooltip(
                        "conversationDetails.overallCustomerSentiments.improvingSentimentTrendTooltip"
                    );
                }
                // slightly improving
                else if (sentimentTrend <= 0.55 && sentimentTrend > 0.2) {
                    sentimentTrendString(
                        ko.i18n.renderString(
                            "conversationDetails.overallCustomerSentiments.slightlyImprovingSentimentTrend"
                        )
                    );
                    sentimentTrendColor("sentiment-trend-slightly-improving");
                    sentimentTrendIcon(
                        OverallSentimentResources
                            .slightlyImprovingSentimentTrend.iconClassName
                    );
                    sentimentTrendTooltip(
                        "conversationDetails.overallCustomerSentiments.improvingSentimentTrendTooltip"
                    );
                }
                // no change
                else if (sentimentTrend <= 0.2 && sentimentTrend >= -0.2) {
                    sentimentTrendString(
                        ko.i18n.renderString(
                            "conversationDetails.overallCustomerSentiments.noChangeSentimentTrend"
                        )
                    );
                    sentimentTrendColor("sentiment-trend-no-change");
                    sentimentTrendIcon(
                        OverallSentimentResources.noChangeSentimentTrend
                            .iconClassName
                    );
                    if (sentimentTrend > 0) {
                        sentimentTrendTooltip(
                            "conversationDetails.overallCustomerSentiments.noChangeImprovedSentimentTrendTooltip"
                        );
                    } else if (sentimentTrend < 0) {
                        sentimentTrendTooltip(
                            "conversationDetails.overallCustomerSentiments.noChangeDeclinedSentimentTrendTooltip"
                        );
                    } else {
                        sentimentTrendTooltip(
                            "conversationDetails.overallCustomerSentiments.noChangeZeroSentimentTrendTooltip"
                        );
                    }
                }
                // slightly declining
                else if (sentimentTrend < -0.2 && sentimentTrend >= -0.55) {
                    sentimentTrendString(
                        ko.i18n.renderString(
                            "conversationDetails.overallCustomerSentiments.slightlyDecliningSentimentTrend"
                        )
                    );
                    sentimentTrendColor("sentiment-trend-slightly-declining");
                    sentimentTrendIcon(
                        OverallSentimentResources
                            .slightlyDecliningSentimentTrend.iconClassName
                    );
                    sentimentTrendTooltip(
                        "conversationDetails.overallCustomerSentiments.decliningSentimentTrendTooltip"
                    );
                }
                // declining
                else if (sentimentTrend < -0.55) {
                    sentimentTrendString(
                        ko.i18n.renderString(
                            "conversationDetails.overallCustomerSentiments.decliningSentimentTrend"
                        )
                    );
                    sentimentTrendColor("sentiment-trend-declining");
                    sentimentTrendIcon(
                        OverallSentimentResources.decliningSentimentTrend
                            .iconClassName
                    );
                    sentimentTrendTooltip(
                        "conversationDetails.overallCustomerSentiments.decliningSentimentTrendTooltip"
                    );
                }
            } else {
                sentimentTrendString("- -");
                sentimentTrendTooltip(
                    "conversationDetails.overallCustomerSentiments.emptyTrend"
                );
            }
            self.overallCustomerSentiments({
                sentimentScoreString: sentimentScoreString,
                sentimentScoreIcon: sentimentScoreIcon,
                sentimentScoreTooltip: sentimentScoreTooltip,
                sentimentTrendString: sentimentTrendString,
                sentimentTrendIcon: sentimentTrendIcon,
                sentimentTrendTooltip: sentimentTrendTooltip,
                sentimentTrendColor: sentimentTrendColor,
                sentimentTrendPercentage: sentimentTrendPercentage,
            });

            //set acoustic information percentage values
            if (conversationMetrics.participantMetrics) {
                const participantMetrics =
                    conversationMetrics.participantMetrics;

                self.maxAcousticDurationPercentage(
                    _.max([...Object.values(participantMetrics), 0])
                );

                const overtalkPercentage = Number.isFinite(
                    participantMetrics.overtalkDurationPercentage
                )
                    ? participantMetrics.overtalkDurationPercentage
                    : 0;
                const overtalkCount = Number.isFinite(
                    participantMetrics.overtalkCount
                )
                    ? participantMetrics.overtalkCount
                    : 0;
                const overtalkLabel = `${self.formatPercentage(
                    overtalkPercentage
                )} (${overtalkCount})`;

                let acousticAnalysisDurations = [
                    {
                        name: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.agentText"
                        ),
                        percent: ko.observable(
                            Number.isFinite(
                                participantMetrics.agentDurationPercentage
                            )
                                ? participantMetrics.agentDurationPercentage
                                : 0
                        ),
                        titleTooltip: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.agentTooltip"
                        ),
                        color: SparkVariables.GENESYS_BLUE_60,
                    },
                    {
                        name: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.customerText"
                        ),
                        percent: ko.observable(
                            Number.isFinite(
                                participantMetrics.customerDurationPercentage
                            )
                                ? participantMetrics.customerDurationPercentage
                                : 0
                        ),
                        titleTooltip: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.customerTooltip"
                        ),
                        color: SparkVariables.GENESYS_BLUE_10,
                    },
                    {
                        name: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.silenceText"
                        ),
                        percent: ko.observable(
                            Number.isFinite(
                                participantMetrics.silenceDurationPercentage
                            )
                                ? participantMetrics.silenceDurationPercentage
                                : 0
                        ),
                        titleTooltip: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.silenceTooltip"
                        ),
                        color: SparkVariables.GENESYS_BLACK_100,
                    },
                    {
                        name: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.ivrText"
                        ),
                        percent: ko.observable(
                            Number.isFinite(
                                participantMetrics.ivrDurationPercentage
                            )
                                ? participantMetrics.ivrDurationPercentage
                                : 0
                        ),
                        titleTooltip: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.ivrTooltip"
                        ),
                        color: SparkVariables.GENESYS_BLACK_100,
                    },
                    {
                        name: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.acdText"
                        ),
                        percent: ko.observable(
                            Number.isFinite(
                                participantMetrics.acdDurationPercentage
                            )
                                ? participantMetrics.acdDurationPercentage
                                : 0
                        ),
                        titleTooltip: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.acdTooltip"
                        ),
                        color: SparkVariables.GENESYS_BLACK_100,
                    },
                    {
                        name: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.otherText"
                        ),
                        percent: ko.observable(
                            Number.isFinite(
                                participantMetrics.otherDurationPercentage
                            )
                                ? participantMetrics.otherDurationPercentage
                                : 0
                        ),
                        titleTooltip: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.otherTooltip"
                        ),
                        color: SparkVariables.GENESYS_BLACK_100,
                    },
                    {
                        name: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.overtalkText"
                        ),
                        percent: ko.observable(
                            Number.isFinite(
                                participantMetrics.overtalkDurationPercentage
                            )
                                ? participantMetrics.overtalkDurationPercentage
                                : 0
                        ),
                        label: overtalkLabel,
                        labelAriaLabel: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.overtalkLabelAriaLabel",
                            {
                                percentageNumber:
                                    self.formatPercentage(overtalkPercentage),
                                overtalkInstance: overtalkCount,
                            }
                        ),
                        titleTooltip: ko.i18n.renderString(
                            "conversationDetails.acousticAnalysis.overtalkTooltip"
                        ),
                        color: SparkVariables.GENESYS_YELLOW_60,
                    },
                ];

                acousticAnalysisDurations = _.orderBy(
                    acousticAnalysisDurations,
                    [(a) => a.percent(), "name"],
                    ["desc", "asc"]
                );
                self.conversationAcousticDurationPercentages(
                    acousticAnalysisDurations
                );
            } else {
                self.maxAcousticDurationPercentage(100);
                self.conversationAcousticDurationPercentages([]);
            }
        } catch (err) {
            self.setOverallCustomerSentimentErrorFallback(err);
        }
    };

    self.getConversationMetrics = async function () {
        speechAndTextAnalyticsService
            .getConversationMetrics(self.conversationId())
            .then((conversationMetrics) => {
                self.conversationMetrics(conversationMetrics);
            })
            .catch((err) => {
                self.conversationMetricsErr(err);
            });
    };
    Promise.resolve(self.getConversationMetrics());

    self.computed(function () {
        const conversation = ko.toJS(conversationObservable);
        const analyticsConversationDetails = ko.toJS(conversationDetailsV6);
        if (conversation) {
            if (self.id !== conversation.id) {
                self.id = conversation.id;
                self.externalTag =
                    conversation.externalTag ||
                    (analyticsConversationDetails &&
                        analyticsConversationDetails.externalTag);
                self.participantViews([]);
                self.internalParticipantViews([]);
                self.externalParticipantViews([]);
            }

            const isActive = isConversationActive(conversation);
            self.active(isActive);

            //get duration
            if (conversation.startTime) {
                const startDate = moment(conversation.startTime);
                self.startDateTime(startDate.toDate());
                self.formattedStartDateTime(
                    ko.i18n.formatDate(
                        self.startDateTime(),
                        timeFormatWithSeconds,
                        null,
                        FULL_DATE_MEDIUM_TIME_OPTIONS
                    )
                );

                if (!isActive) {
                    // Calculate duration for historical conversation
                    const endDate = moment(conversation.endTime);
                    self.endDateTime(endDate.toDate());
                    self.formattedEndDateTime(
                        ko.i18n.formatDate(
                            self.endDateTime(),
                            timeFormatWithSeconds,
                            null,
                            FULL_DATE_MEDIUM_TIME_OPTIONS
                        )
                    );
                    self.duration(endDate.diff(startDate));
                }
            }

            if (
                conversation.participants &&
                (self.conversationMetrics() || self.conversationMetricsErr())
            ) {
                _.each(conversation.participants, function (participant) {
                    let participantView = _.find(
                        self.participantViews(),
                        function (view) {
                            return view.participantId === participant.id;
                        }
                    );

                    let analyticsParticipant = null;
                    if (
                        analyticsConversationDetails &&
                        analyticsConversationDetails.participants
                    ) {
                        analyticsParticipant = _.find(
                            analyticsConversationDetails.participants,
                            { participantId: participant.id }
                        );
                    }

                    self.isAlreadyCoached =
                        coachedParticipants != null &&
                        coachedParticipants().indexOf(participant.userId) >= 0;
                    self.isAlreadyMonitored =
                        monitoredParticipants != null &&
                        monitoredParticipants().indexOf(participant.userId) >=
                            0;
                    if (!participantView) {
                        participantView = self.subView(
                            ParticipantView,
                            conversation.id,
                            participant,
                            analyticsParticipant,
                            isCurrentUserOnCall,
                            hasMonitorCallPermission,
                            hasCoachCallPermission,
                            hasBargeInCallPermission,
                            hasMonitorMessagePermission,
                            hasAgentLevelScreenMonitorPermission,
                            hasConversationLevelScreenMonitorPermission,
                            options,
                            self.isAnyInternalParticipantMonitored,
                            self.isAnyInternalParticipantCoached,
                            self.isAnyInternalParticipantBargedIn,
                            self.isExternalParticipantMonitored,
                            self.isAlreadyCoached,
                            self.isAlreadyMonitored,
                            self.conversationMetrics(),
                            self.conversationMetricsErr(),
                            self.conversationType,
                            conversation.divisionIds
                        );
                        self.participantViews.push(participantView);
                        if (participantView.isInternal()) {
                            self.internalParticipantViews.push(participantView);
                        } else if (participantView.isExternal()) {
                            self.externalParticipantViews.push(participantView);
                        }
                    } else {
                        participantView.updateParticipant(
                            participant,
                            analyticsParticipant,
                            self.isAlreadyCoached,
                            self.isAlreadyMonitored
                        );
                    }
                    if (
                        participantView.dnis &&
                        !/[a-z]/i.test(participantView.dnis)
                    ) {
                        let dnisValue = participantView.dnis.replace(
                            /[-+ )(]/g,
                            ""
                        );
                        if (!_.includes(self.dnis(), dnisValue)) {
                            if (!_.isEmpty(self.dnis())) {
                                dnisValue =
                                    self.dnis() +
                                    ko.i18n.renderString(
                                        "interaction.list.delimiter"
                                    ) +
                                    dnisValue;
                            }
                            self.dnis(dnisValue);
                        }
                    }

                    if (
                        participantView.ani &&
                        !/[a-z]/i.test(participantView.ani)
                    ) {
                        let aniValue = participantView.ani.replace(
                            /[-+ )(]/g,
                            ""
                        );
                        if (!_.includes(self.ani(), aniValue)) {
                            if (!_.isEmpty(self.ani())) {
                                aniValue =
                                    self.ani() +
                                    ko.i18n.renderString(
                                        "interaction.list.delimiter"
                                    ) +
                                    aniValue;
                            }
                            self.ani(aniValue);
                        }
                    }

                    if (
                        participant.queueName &&
                        !_.includes(self.queueArray(), participant.queueName)
                    ) {
                        self.queueArray.push(participant.queueName);
                        addQueueName(participant.queueName);
                    }

                    if (participant.wrapup && participant.wrapup.code) {
                        addWrapupName(getWrapupName(participant.wrapup));
                    }

                    if (participant.calls && participant.calls.length > 0) {
                        if (
                            !_.isUndefined(participant.calls[0].direction) &&
                            !self.bothDirections()
                        ) {
                            setDirection(participant.calls[0].direction);
                        }
                        self.hasCalls(true);
                        self.isAcdVoicemail(
                            options.recordingManagerMediaTypes.isAcdvoicemail()
                        );
                    }

                    // Set whether the conversation has a particular media type based on this participant.
                    self.hasChats(
                        self.hasChats() ||
                            (!!participant.chats &&
                                participant.chats.length > 0)
                    );
                    self.hasEmails(
                        self.hasEmails() ||
                            (!!participant.emails &&
                                participant.emails.length > 0)
                    );
                    self.hasCallbacks(
                        self.hasCallbacks() ||
                            (!!participant.callbacks &&
                                participant.callbacks.length > 0)
                    );
                    self.hasCobrowseSessions(
                        self.hasCobrowseSessions() ||
                            (!!participant.cobrowsesessions &&
                                participant.cobrowsesessions.length > 0)
                    );

                    const videoChatFTEnabled = featureToggleService.isEnabled(
                        FeatureToggle.PURE_5422_VIDEO_CHAT
                    );
                    self.hasVideos(
                        self.hasVideos() ||
                            (videoChatFTEnabled &&
                                !!participant.videos &&
                                participant.videos.length > 0)
                    );
                    self.hasScreenshares(
                        self.hasScreenshares() ||
                            (!!participant.screenshares &&
                                participant.screenshares.length > 0)
                    );
                    self.hasMessages(
                        self.hasMessages() ||
                            (!!participant.messages &&
                                participant.messages.length > 0)
                    );

                    if (self.isScreenMonitoringEnabled) {
                        const screenMonitorings = ko.unwrap(
                            participant.screenMonitorings
                        );
                        self.hasScreenMonitorings(
                            self.hasScreenMonitorings() ||
                                (!!screenMonitorings &&
                                    screenMonitorings.length > 0)
                        );
                    }

                    // Support showing message type -- sms, line, facebook twitter
                    if (
                        !self.messageType() &&
                        participantView.getMessageType()
                    ) {
                        const isTwitterXEnabled =
                            featureToggleService.isEnabled(
                                FeatureToggle.PURE_5797_MESSAGING_TWITTER_PRIVATE_DIRECT_MESSAGES
                            );

                        const messageTypeKey =
                            isTwitterXEnabled &&
                            participantView.getMessageType() === "twitter"
                                ? "conversationDetails.messageType.x"
                                : `conversationDetails.messageType.${participantView.getMessageType()}`;

                        const type = ko.i18n.renderString(messageTypeKey);

                        if (type) {
                            self.messageType(type);
                        }
                    }
                });

                // Recompute participantAttributesPresent after updating all participants
                const hasAnyAttributes = _.some(
                    self.participantViews(),
                    function (pv) {
                        return pv.attributes().length > 0;
                    }
                );
                self.participantAttributesPresent(hasAnyAttributes);

                const isEngagementSourceToggleEnabled =
                    featureToggleService.isEnabled(
                        FeatureToggle.PURE_5534_OPEN_MESSAGING_ENGAGEMENT_SOURCE_TAGGING
                    );

                if (isEngagementSourceToggleEnabled) {
                    setEngagementSources(conversation);
                }

                if (!isActive) {
                    if (self.conversationMetricsErr()) {
                        self.setOverallCustomerSentimentErrorFallback(
                            self.conversationMetricsErr()
                        );
                        self.setEmpathyErrorFallback(
                            self.conversationMetricsErr()
                        );
                    } else if (self.conversationMetrics()) {
                        self.setOverallCustomerSentiment(
                            self.conversationMetrics()
                        );
                        self.setEmpathy(self.conversationMetrics());
                    }
                }

                const internals = self.internalParticipantViews();
                const externals = self.externalParticipantViews();

                const activeInternal = _.filter(internals, (ipv) =>
                    ipv.isActive()
                );
                if (internals.length > 1) {
                    self.multipleAgentEmpathies(true);
                }

                self.agentNames(getParticipantViewNames(internals));
                self.activeAgentNames(getParticipantViewNames(activeInternal));

                if (internals.length > 0) {
                    self.agentName(internals[0].displayString());
                } else {
                    self.agentName(ko.i18n.renderString("search.noName"));
                }

                if (externals.length > 0) {
                    self.externalName(externals[0].displayString());
                } else {
                    self.externalName(ko.i18n.renderString("search.noName"));
                }
            }
            if (!conversation.id) {
                //We will not have 'conversation.id' for aged Interaction with 404 response
                self.internalUsers([]);
                self.externalContacts([]);
                self.hasMessages(
                    options.recordingManagerMediaTypes.hasMessages()
                );
                self.hasEmails(options.recordingManagerMediaTypes.hasEmails());
                self.hasChats(options.recordingManagerMediaTypes.hasChats());
                self.hasCalls(options.recordingManagerMediaTypes.hasCalls());
                self.internalUsers(_.uniq(options.internalUsers(), "id"));
                self.participantViews([]);
                self.internalParticipantViews([]);
                self.externalParticipantViews([]);
                self.internalUsers().forEach((obj) => {
                    const isAlreadyCoached =
                        coachedParticipants != null &&
                        coachedParticipants().indexOf(obj.userId) >= 0;
                    const isAlreadyMonitored =
                        monitoredParticipants != null &&
                        monitoredParticipants().indexOf(obj.userId) >= 0;
                    const participantView = self.subView(
                        ParticipantView,
                        self.conversationId(),
                        obj,
                        null,
                        isCurrentUserOnCall,
                        hasMonitorCallPermission,
                        hasCoachCallPermission,
                        hasBargeInCallPermission,
                        hasMonitorMessagePermission,
                        hasAgentLevelScreenMonitorPermission,
                        hasConversationLevelScreenMonitorPermission,
                        options,
                        self.isAnyInternalParticipantMonitored,
                        self.isAnyInternalParticipantCoached,
                        self.isAnyInternalParticipantBargedIn,
                        self.isExternalParticipantMonitored,
                        isAlreadyCoached,
                        isAlreadyMonitored,
                        self.conversationMetrics(),
                        self.conversationMetricsErr(),
                        self.conversationType,
                        conversation.divisionIds
                    );
                    self.participantViews.push(participantView);
                    participantView.isInternal(true);
                    participantView.displayName = obj.name;
                    self.internalParticipantViews.push(participantView);
                });
                self.externalContacts(_.uniq(options.externalContacts(), "id"));
                self.externalContacts().forEach((obj) => {
                    const participantView = self.subView(
                        ParticipantView,
                        self.conversationId(),
                        obj,
                        null,
                        isCurrentUserOnCall,
                        hasMonitorCallPermission,
                        hasCoachCallPermission,
                        hasBargeInCallPermission,
                        hasMonitorMessagePermission,
                        hasAgentLevelScreenMonitorPermission,
                        hasConversationLevelScreenMonitorPermission,
                        options,
                        self.isAnyInternalParticipantMonitored,
                        self.isAnyInternalParticipantCoached,
                        self.isAnyInternalParticipantBargedIn,
                        self.isExternalParticipantMonitored,
                        null,
                        null,
                        self.conversationMetrics(),
                        self.conversationMetricsErr(),
                        self.conversationType,
                        conversation.divisionIds
                    );
                    self.participantViews.push(participantView);
                    participantView.isExternal(true);
                    self.externalParticipantViews.push(participantView);
                });
            }
        }
    });
});

export default ConversationDetailsView;
